import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import axios from 'axios';
import { 
  Building2, Users, Plus, ChevronRight, LogOut, 
  Search, RefreshCw, Edit, X, Check, Upload,
  Calendar, MapPin, FileText, User, Mail, Shield, Table,
  AlertTriangle, Clock, Camera, CheckCircle2, Brain, Eye, Target,
  Package, Truck, DollarSign, MessageCircle, Phone, Trash2, Tag,
  Layers, XCircle, Ruler, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import CatalogWizardModal from '@/components/CatalogWizardModal';
import { ContractConceptsList, formatMXN } from '@/components/ContractConceptsList';
import { offlineDb } from '@/offline/db';
import { syncManager, shouldQueue, postWithTimeout } from '@/offline/syncManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// GenerIA Brand Logo URL
const GENERIA_LOGO = "https://customer-assets.emergentagent.com/job_inspeccion-pwa/artifacts/qhvfjidw_IMG_8814.jpeg";

// ============= CONTRACT WIZARD MODAL =============
const ContractWizardModal = ({ onClose, onSuccess, users, editContract = null, onOpenManualConcept, onEditConcept, conceptsRefreshKey, onOpenHub }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCatalogWizard, setShowCatalogWizard] = useState(false);
  const [formData, setFormData] = useState({
    name: editContract?.name || '',
    numero_contrato: editContract?.numero_contrato || '',
    contractor_id: editContract?.contractor_id || '',
    inspector_ids: editContract?.inspector_ids || [],
    fecha_inicio: editContract?.fecha_inicio || '',
    fecha_fin: editContract?.fecha_fin || '',
    ubicacion: editContract?.ubicacion || '',
  });

  const contractors = users.filter(u => u.role === 'contractor');
  const inspectors = users.filter(u => u.role === 'inspector');

  // Get assigned user names for display
  const getContractorName = () => {
    const contractor = users.find(u => u.id === (editContract?.contractor_id || formData.contractor_id));
    return contractor?.name || 'Sin asignar';
  };

  const getInspectorNames = () => {
    const ids = editContract?.inspector_ids || formData.inspector_ids || [];
    return ids.map(id => {
      const inspector = users.find(u => u.id === id);
      return inspector?.name || id;
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleInspectorToggle = (inspectorId) => {
    setFormData(prev => ({
      ...prev,
      inspector_ids: prev.inspector_ids.includes(inspectorId)
        ? prev.inspector_ids.filter(id => id !== inspectorId)
        : [...prev.inspector_ids, inspectorId]
    }));
  };

  const handleNext = () => {
    if (!formData.name.trim()) {
      toast.error('El nombre del contrato es requerido');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let projectId = editContract?.id;
      
      if (!editContract) {
        // Create new project
        const response = await axios.post(`${API}/projects`, formData);
        projectId = response.data.id;
        toast.success('Contrato creado exitosamente');
      } else {
        // Update existing project
        await axios.put(`${API}/admin/projects/${projectId}`, formData);
        toast.success('Contrato actualizado exitosamente');
      }
      
      // Note: Catalog upload is now done separately via CatalogWizardModal
      // from the contract detail page admin panel
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D1520] rounded-2xl border border-[#1E3550] w-full max-w-2xl max-h-[90vh] overflow-hidden" data-testid="contract-wizard-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#E2EAF4]">
              {editContract ? 'Editar Contrato' : 'Nuevo Contrato'}
            </h2>
            <p className="text-[#5A7A9A] text-sm">Paso {step} de 2</p>
          </div>
          <div className="flex items-center gap-2">
            {editContract && onOpenHub && (
              <Button
                size="sm"
                onClick={() => onOpenHub(editContract)}
                className="bg-[#00CFFF] hover:bg-[#00CFFF]/80 text-[#060C18] font-semibold"
                title="Abrir la vista del contrato (CAPTURAR / SÁBANA / GENERADORES / PRESUPUESTO / OPORTUNIDADES)"
                data-testid="btn-open-contract-hub"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Abrir contrato
              </Button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-[#1E3550] rounded-lg text-[#E2EAF4]">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-[#1E3550]">
          <div 
            className="h-full bg-[#0066FF] transition-all"
            style={{ width: step === 1 ? '50%' : '100%' }}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {step === 1 ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-[#E2EAF4] mb-4">Datos del Contrato</h3>
              
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
                  Nombre del Contrato *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ej: Edificio Municipal Norte"
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                  data-testid="input-contract-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
                  Número de Contrato Oficial
                </label>
                <Input
                  value={formData.numero_contrato}
                  onChange={(e) => handleInputChange('numero_contrato', e.target.value)}
                  placeholder="Ej: CONT-2026-001"
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                  data-testid="input-contract-number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
                  Contratista <span className="text-[#5A7A9A]/70">(opcional, se puede asignar después)</span>
                </label>
                <select
                  value={formData.contractor_id}
                  onChange={(e) => handleInputChange('contractor_id', e.target.value)}
                  className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                  data-testid="select-contractor"
                >
                  <option value="">Sin asignar</option>
                  {contractors.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                  ))}
                </select>
                {contractors.length === 0 && (
                  <p className="text-xs text-[#5A7A9A] mt-1" data-testid="no-contractors-hint">
                    Aún no hay contratistas. Crea el contrato y luego el usuario en USUARIOS seleccionando este contrato.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-2">
                  Residentes Asignados <span className="text-[#5A7A9A]/70">(opcional)</span>
                </label>
                <div className="border border-[#1E3550] rounded-lg p-3 max-h-40 overflow-y-auto space-y-2 bg-[#0A1422]">
                  {inspectors.length === 0 ? (
                    <p className="text-[#5A7A9A] text-sm" data-testid="no-inspectors-hint">
                      No hay residentes disponibles. Puedes asignarlos después desde USUARIOS.
                    </p>
                  ) : (
                    inspectors.map(inspector => (
                      <label 
                        key={inspector.id} 
                        className="flex items-center gap-3 p-2 hover:bg-[#1E3550] rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.inspector_ids.includes(inspector.id)}
                          onChange={() => handleInspectorToggle(inspector.id)}
                          className="w-4 h-4 text-[#0066FF] rounded bg-[#0A1422] border-[#1E3550]"
                        />
                        <span className="text-sm text-[#E2EAF4]">{inspector.name}</span>
                        <span className="text-xs text-[#5A7A9A]">{inspector.email}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
                    Fecha Inicio
                  </label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => handleInputChange('fecha_inicio', e.target.value)}
                    className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                    data-testid="input-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
                    Fecha Fin
                  </label>
                  <Input
                    type="date"
                    value={formData.fecha_fin}
                    onChange={(e) => handleInputChange('fecha_fin', e.target.value)}
                    className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                    data-testid="input-end-date"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
                  Ubicación / Descripción
                </label>
                <textarea
                  value={formData.ubicacion}
                  onChange={(e) => handleInputChange('ubicacion', e.target.value)}
                  placeholder="Dirección o descripción del proyecto..."
                  className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] placeholder:text-[#5A7A9A] focus:ring-2 focus:ring-[#0066FF] min-h-[80px]"
                  data-testid="input-location"
                />
              </div>
            </div>
          ) : editContract ? (
            /* EDITING EXISTING CONTRACT - Show details + catalog button */
            <div className="space-y-6">
              <h3 className="font-semibold text-[#E2EAF4] mb-4">Detalles del Contrato</h3>
              
              {/* Contract Info Summary */}
              <div className="bg-[#0A1422] rounded-lg p-4 space-y-3 border border-[#1E3550]">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5A7A9A]">Número de contrato:</span>
                  <span className="font-medium text-[#E2EAF4]">{editContract.numero_contrato || 'Sin número'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5A7A9A]">Conceptos cargados:</span>
                  <span className="font-mono font-bold text-lg text-[#0066FF]">{editContract.concept_count || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5A7A9A]">Avance:</span>
                  <span className="font-medium text-[#E2EAF4]">{editContract.progress?.toFixed(1) || 0}%</span>
                </div>
              </div>

              {/* Assigned Users */}
              <div className="space-y-3">
                <h4 className="font-medium text-[#E2EAF4]">Usuarios Asignados</h4>
                <div className="bg-[#0A1422] border border-[#1E3550] rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-[#0066FF]" />
                    <span className="text-sm text-[#5A7A9A]">Contratista:</span>
                    <span className="font-medium text-[#E2EAF4]">{getContractorName()}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-[#22C55E] mt-0.5" />
                    <span className="text-sm text-[#5A7A9A]">Residentes:</span>
                    <div className="flex flex-wrap gap-1">
                      {getInspectorNames().length > 0 ? (
                        getInspectorNames().map((name, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-[#22C55E]/20 text-[#22C55E] rounded text-xs">
                            {name}
                          </span>
                        ))
                      ) : (
                        <span className="text-[#5A7A9A] text-sm">Sin asignar</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Catalog Upload Button */}
              <div className="bg-[#A855F7]/10 border-2 border-[#A855F7]/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#A855F7]/20 rounded-lg flex items-center justify-center">
                    <Table className="w-6 h-6 text-[#A855F7]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#A855F7]">Catálogo de Conceptos</h4>
                    <p className="text-sm text-[#A855F7]/70">
                      {editContract.concept_count > 0 
                        ? `${editContract.concept_count} conceptos cargados`
                        : 'Sin catálogo cargado'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowCatalogWizard(true)}
                    className="flex-1 bg-[#A855F7] hover:bg-[#A855F7]/80 text-white"
                    data-testid="btn-load-catalog"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {editContract.concept_count > 0 ? 'Recargar Catálogo' : 'Cargar Catálogo'}
                  </Button>
                  <Button
                    onClick={() => onOpenManualConcept(editContract.id)}
                    variant="outline"
                    className="border-[#A855F7] text-[#A855F7] hover:bg-[#A855F7]/10"
                    data-testid="btn-add-manual-concept"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Manual
                  </Button>
                </div>
                <p className="text-xs text-[#A855F7]/60 mt-2 text-center">
                  Sube un archivo Excel o agrega conceptos manualmente
                </p>
                <ContractConceptsList
                  projectId={editContract.id}
                  refreshKey={conceptsRefreshKey}
                  onEdit={onEditConcept}
                  onDeleted={onSuccess}
                />
              </div>
            </div>
          ) : (
            /* CREATING NEW CONTRACT - Show info about loading catalog later */
            <div className="space-y-4">
              <h3 className="font-semibold text-[#E2EAF4] mb-4">Catálogo de Conceptos</h3>
              
              <div className="bg-[#0066FF]/10 border border-[#0066FF]/30 rounded-xl p-6 text-center">
                <Upload className="w-12 h-12 text-[#0066FF] mx-auto mb-4" />
                <p className="text-[#E2EAF4] font-medium mb-2">
                  El catálogo se carga después de crear el contrato
                </p>
                <p className="text-sm text-[#5A7A9A]">
                  Una vez creado el contrato, podrás cargar el catálogo desde este panel.
                  El sistema permite mapear las columnas del Excel según tu formato.
                </p>
              </div>

              <div className="bg-[#0A1422] border border-[#1E3550] rounded-lg p-4">
                <p className="text-sm text-[#E2EAF4]">
                  <strong>Proceso de carga:</strong>
                </p>
                <ol className="text-sm text-[#5A7A9A] mt-2 space-y-1 list-decimal list-inside">
                  <li>Crear el contrato (este paso)</li>
                  <li>Abrir el contrato creado</li>
                  <li>Usar "Cargar Catálogo"</li>
                  <li>Mapear las columnas de tu Excel</li>
                  <li>Confirmar la carga</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#1E3550] px-6 py-4 flex justify-between bg-[#0A1422]">
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={onClose} className="text-[#E2EAF4] hover:bg-[#1E3550]">Cancelar</Button>
              <Button onClick={handleNext} className="bg-[#0066FF] hover:bg-[#0066FF]/80">
                Siguiente
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep(1)} className="text-[#E2EAF4] hover:bg-[#1E3550]">
                Anterior
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                data-testid="contract-wizard-submit"
                className="bg-[#22C55E] hover:bg-[#22C55E]/80"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {editContract ? 'Guardar Cambios' : 'Crear Contrato'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Catalog Wizard Modal */}
      {showCatalogWizard && editContract && (
        <CatalogWizardModal
          projectId={editContract.id}
          projectName={editContract.name}
          onClose={() => setShowCatalogWizard(false)}
          onSuccess={() => {
            setShowCatalogWizard(false);
            onSuccess(); // Refresh the projects list
          }}
        />
      )}
    </div>
  );
};

// ============= USER MODAL =============
const UserModal = ({ onClose, onSuccess, projects, editUser = null }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: editUser?.name || '',
    email: editUser?.email || '',
    password: '',
    role: editUser?.role || 'inspector',
    assigned_projects: editUser?.assigned_projects || [],
    status: editUser?.status || 'active',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProjectToggle = (projectId) => {
    setFormData(prev => ({
      ...prev,
      assigned_projects: prev.assigned_projects.includes(projectId)
        ? prev.assigned_projects.filter(id => id !== projectId)
        : [...prev.assigned_projects, projectId]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    if (!editUser && !formData.password) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }

    setLoading(true);
    try {
      if (editUser) {
        await axios.put(`${API}/admin/users/${editUser.id}`, formData);
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/admin/users`, formData);
        toast.success('Usuario creado');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D1520] rounded-2xl border border-[#1E3550] w-full max-w-lg max-h-[90vh] overflow-hidden" data-testid="user-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#E2EAF4]">
            {editUser ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#1E3550] rounded-lg text-[#E2EAF4]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
              Nombre Completo *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Juan Pérez"
              className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
              data-testid="input-user-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
              Email *
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="usuario@empresa.com"
              className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
              data-testid="input-user-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
              {editUser ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña Temporal *'}
            </label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder="********"
              className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
              data-testid="input-user-password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
              Rol *
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
              data-testid="select-user-role"
              disabled={editUser?.role === 'super_admin'}
            >
              <option value="inspector">Residente</option>
              <option value="contractor">Contratista</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-2">
              Contratos Asignados <span className="text-[#5A7A9A]/70">(opcional, se puede asignar después)</span>
            </label>
            <div className="border border-[#1E3550] rounded-lg p-3 max-h-40 overflow-y-auto space-y-2 bg-[#0A1422]">
              {projects.length === 0 ? (
                <p className="text-[#5A7A9A] text-sm" data-testid="no-projects-hint">
                  No hay contratos disponibles. Puedes crear el usuario ahora y asignarlo al editar el contrato.
                </p>
              ) : (
                projects.map(project => (
                  <label 
                    key={project.id} 
                    className="flex items-center gap-3 p-2 hover:bg-[#1E3550] rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.assigned_projects.includes(project.id)}
                      onChange={() => handleProjectToggle(project.id)}
                      className="w-4 h-4 text-[#0066FF] rounded bg-[#0A1422] border-[#1E3550]"
                    />
                    <span className="text-sm text-[#E2EAF4]">{project.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {editUser && (
            <div>
              <label className="block text-sm font-medium text-[#5A7A9A] mb-1">
                Estado
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                data-testid="select-user-status"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#1E3550] px-6 py-4 flex justify-end gap-3 bg-[#0A1422]">
          <Button variant="ghost" onClick={onClose} className="text-[#E2EAF4] hover:bg-[#1E3550]">Cancelar</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            data-testid="user-modal-submit"
            className="bg-[#22C55E] hover:bg-[#22C55E]/80"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {editUser ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============= MAIN ADMIN DASHBOARD =============
export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { setSelectedProject, setSelectedConcept } = useApp();
  const [activeTab, setActiveTab] = useState('contratos');
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState({
    active_contracts: 0,
    captures_this_week: 0,
    active_residentes: 0,
    closed_weeks: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Intelligence state
  const [intelligenceStats, setIntelligenceStats] = useState(null);
  const [intelligenceResults, setIntelligenceResults] = useState([]);
  const [loadingIntelligence, setLoadingIntelligence] = useState(false);
  
  // Figure Predictions state (Vision vs Residente)
  const [figurePredictionStats, setFigurePredictionStats] = useState(null);
  const [figurePredictions, setFigurePredictions] = useState([]);
  const [selectedFigurePrediction, setSelectedFigurePrediction] = useState(null);
  const [showFigurePredictionDetail, setShowFigurePredictionDetail] = useState(false);
  const [loadingFigureDetail, setLoadingFigureDetail] = useState(false);
  const [visionImageDims, setVisionImageDims] = useState({ width: 0, height: 0 });
  const [residenteImageDims, setResidenteImageDims] = useState({ width: 0, height: 0 });
  const visionImgRef = useRef(null);
  const residenteImgRef = useRef(null);
  
  // Measurement Predictions state (Vision vs Residente - Mediciones)
  const [measurementPredictionStats, setMeasurementPredictionStats] = useState(null);
  const [measurementPredictions, setMeasurementPredictions] = useState([]);
  const [selectedMeasurementPrediction, setSelectedMeasurementPrediction] = useState(null);
  const [showMeasurementPredictionDetail, setShowMeasurementPredictionDetail] = useState(false);
  const [loadingMeasurementDetail, setLoadingMeasurementDetail] = useState(false);
  const [measurementVisionImageDims, setMeasurementVisionImageDims] = useState({ width: 0, height: 0 });
  const [measurementResidenteImageDims, setMeasurementResidenteImageDims] = useState({ width: 0, height: 0 });
  const measurementVisionImgRef = useRef(null);
  const measurementResidenteImgRef = useRef(null);
  
  // Oportunidades state
  const [oportunidadesStats, setOportunidadesStats] = useState(null);
  const [oportunidadesTengo, setOportunidadesTengo] = useState([]);
  const [oportunidadesNecesito, setOportunidadesNecesito] = useState([]);
  const [oportunidadesMatches, setOportunidadesMatches] = useState([]);
  const [oportunidadesInterests, setOportunidadesInterests] = useState([]);
  const [loadingOportunidades, setLoadingOportunidades] = useState(false);
  const [oportunidadesSubTab, setOportunidadesSubTab] = useState('tengo');
  const [selectedOportunidadDetail, setSelectedOportunidadDetail] = useState(null);
  const [oportunidadDetailType, setOportunidadDetailType] = useState('tengo');
  
  // Modals
  const [showContractWizard, setShowContractWizard] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  // Manual concept modal
  const [showManualConceptModal, setShowManualConceptModal] = useState(false);
  const [manualConceptProjectId, setManualConceptProjectId] = useState(null);
  const [manualConceptData, setManualConceptData] = useState({
    clave: '',
    descripcion: '',
    unidad: 'm²',
    cantidad_total: '',
    precio_unitario: '',
    area: '',
    partida: '',
    cd_mo: '',
    cd_materiales: '',
    cd_total_manual: '',
    pu_mo: '',
    pu_con_material: ''
  });
  const [savingConcept, setSavingConcept] = useState(false);
  const [editingConcept, setEditingConcept] = useState(null); // null = crear, objeto = editar
  const [conceptsRefreshKey, setConceptsRefreshKey] = useState(0);
  const [existingAreas, setExistingAreas] = useState([]); // Áreas ya usadas en el contrato (autocompletar)
  const [existingPartidas, setExistingPartidas] = useState([]); // Partidas ya usadas en el contrato

  // Cargar áreas/partidas existentes del contrato al abrir el modal de concepto manual
  useEffect(() => {
    if (!showManualConceptModal || !manualConceptProjectId) return;
    let cancelled = false;
    (async () => {
      let all = [];
      try {
        const res = await axios.get(`${API}/concepts`, { params: { project_id: manualConceptProjectId }, timeout: 8000 });
        all = res.data || [];
      } catch (e) { /* offline: se usa lo local */ }
      try {
        const local = await offlineDb.getAllConcepts?.();
        if (local) all = [...all, ...local.filter((c) => c.project_id === manualConceptProjectId).map((c) => c.payload)];
      } catch (e) { /* sin datos locales */ }
      const dedupe = (vals) => {
        const seen = new Map();
        vals.map((v) => (v || '').trim()).filter(Boolean).forEach((v) => { const k = v.toLowerCase(); if (!seen.has(k)) seen.set(k, v); });
        return [...seen.values()].sort((a, b) => a.localeCompare(b, 'es'));
      };
      if (!cancelled) {
        setExistingAreas(dedupe(all.map((c) => c.area)));
        setExistingPartidas(dedupe(all.map((c) => c.partida)));
      }
    })();
    return () => { cancelled = true; };
  }, [showManualConceptModal, manualConceptProjectId, conceptsRefreshKey]);

  // Image load handlers for figure comparison modal (same as ConstructionCanvas)
  const handleVisionImageLoad = useCallback(() => {
    if (!visionImgRef.current) return;
    const rect = visionImgRef.current.getBoundingClientRect();
    setVisionImageDims({ width: rect.width, height: rect.height });
  }, []);

  const handleResidenteImageLoad = useCallback(() => {
    if (!residenteImgRef.current) return;
    const rect = residenteImgRef.current.getBoundingClientRect();
    setResidenteImageDims({ width: rect.width, height: rect.height });
  }, []);

  // Convert puntos from 0-1 range to pixel coordinates (same logic as ConstructionCanvas shapeToPixel)
  const puntosToPixels = useCallback((puntos, displayWidth, displayHeight) => {
    if (!puntos || displayWidth <= 0 || displayHeight <= 0) return [];
    return puntos.map(p => ({
      x: (p.x * 100 / 100) * displayWidth,  // puntos are 0-1, convert to pixels
      y: (p.y * 100 / 100) * displayHeight
    }));
  }, []);

  // Image load handlers for measurement comparison modal
  const handleMeasurementVisionImageLoad = useCallback(() => {
    if (!measurementVisionImgRef.current) return;
    const rect = measurementVisionImgRef.current.getBoundingClientRect();
    setMeasurementVisionImageDims({ width: rect.width, height: rect.height });
  }, []);

  const handleMeasurementResidenteImageLoad = useCallback(() => {
    if (!measurementResidenteImgRef.current) return;
    const rect = measurementResidenteImgRef.current.getBoundingClientRect();
    setMeasurementResidenteImageDims({ width: rect.width, height: rect.height });
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsRes, usersRes, kpisRes] = await Promise.all([
        axios.get(`${API}/admin/projects`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/dashboard-kpis`)
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadIntelligence = async () => {
    setLoadingIntelligence(true);
    try {
      const [statsRes, resultsRes, figPredStatsRes, figPredRes, measPredStatsRes, measPredRes] = await Promise.all([
        axios.get(`${API}/admin/intelligence/stats`),
        axios.get(`${API}/admin/intelligence/results?limit=50`),
        axios.get(`${API}/admin/intelligence/figure-prediction-stats`),
        axios.get(`${API}/admin/intelligence/figure-predictions?limit=50`),
        axios.get(`${API}/admin/intelligence/measurement-prediction-stats`),
        axios.get(`${API}/admin/intelligence/measurement-predictions?limit=50`)
      ]);
      setIntelligenceStats(statsRes.data);
      setIntelligenceResults(resultsRes.data);
      setFigurePredictionStats(figPredStatsRes.data);
      setFigurePredictions(figPredRes.data);
      setMeasurementPredictionStats(measPredStatsRes.data);
      setMeasurementPredictions(measPredRes.data);
    } catch (error) {
      console.error('Error loading intelligence data:', error);
      // Silent failure - don't show toast as this is optional feature
    } finally {
      setLoadingIntelligence(false);
    }
  };

  // Load intelligence data when tab changes
  useEffect(() => {
    if (activeTab === 'intelligence') {
      loadIntelligence();
    }
  }, [activeTab]);

  const loadOportunidades = async () => {
    setLoadingOportunidades(true);
    try {
      const [statsRes, tengoRes, necesitoRes, matchesRes, interestsRes] = await Promise.all([
        axios.get(`${API}/oportunidades/stats`),
        axios.get(`${API}/oportunidades/admin/tengo`),
        axios.get(`${API}/oportunidades/admin/necesito`),
        axios.get(`${API}/oportunidades/admin/matches`),
        axios.get(`${API}/oportunidades/admin/interests`)
      ]);
      setOportunidadesStats(statsRes.data);
      setOportunidadesTengo(tengoRes.data);
      setOportunidadesNecesito(necesitoRes.data);
      setOportunidadesMatches(matchesRes.data);
      setOportunidadesInterests(interestsRes.data);
    } catch (error) {
      console.error('Error loading oportunidades data:', error);
    } finally {
      setLoadingOportunidades(false);
    }
  };

  // Load oportunidades data when tab changes
  useEffect(() => {
    if (activeTab === 'oportunidades') {
      loadOportunidades();
    }
  }, [activeTab]);

  const handleApproveTengo = async (listingId) => {
    try {
      const listing = oportunidadesTengo.find(l => l.id === listingId);
      const response = await axios.put(`${API}/oportunidades/tengo/${listingId}/approve`);
      
      // Check if auto match was found
      if (response.data.auto_match && listing) {
        const priceText = listing.precio ? `$${listing.precio.toLocaleString()}` : 'Sin precio';
        const message = encodeURIComponent(
          `🤖 Match automático detectado:\nTIENE: ${listing.material} · ${listing.ubicacion} · ${priceText}\nVENDEDOR: ${listing.user_name}\nRevisa en Admin Dashboard`
        );
        window.open(`https://wa.me/525650192332?text=${message}`, '_blank');
        toast.success('Oferta aprobada - ¡Match encontrado!');
      } else {
        toast.success('Oferta aprobada');
      }
      loadOportunidades();
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  const handleRejectTengo = async (listingId) => {
    try {
      await axios.put(`${API}/oportunidades/tengo/${listingId}/reject`);
      toast.success('Oferta rechazada');
      loadOportunidades();
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  const handleDeleteTengo = async (listingId) => {
    if (!window.confirm('¿Eliminar esta oferta permanentemente?')) return;
    try {
      await axios.delete(`${API}/oportunidades/tengo/${listingId}`);
      toast.success('Oferta eliminada');
      loadOportunidades();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteNecesito = async (listingId) => {
    if (!window.confirm('¿Eliminar esta solicitud permanentemente?')) return;
    try {
      await axios.delete(`${API}/oportunidades/necesito/${listingId}`);
      toast.success('Solicitud eliminada');
      loadOportunidades();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleApproveMatch = async (match) => {
    try {
      await axios.put(`${API}/oportunidades/interest/${match.id}/approve`);
      
      // Send WhatsApp to seller
      if (match.seller_name) {
        const sellerMsg = encodeURIComponent(
          `✅ ¡Buenas noticias! Alguien está interesado en tu material "${match.material}" en GenerIA Oportunidades. Te contactaremos pronto con los detalles.`
        );
        window.open(`https://wa.me/525650192332?text=${sellerMsg}`, '_blank');
      }
      
      toast.success('Match aprobado - WhatsApp enviado');
      loadOportunidades();
    } catch (error) {
      toast.error('Error al aprobar match');
    }
  };

  const handleRejectMatch = async (matchId) => {
    if (!window.confirm('¿Rechazar este match?')) return;
    try {
      await axios.put(`${API}/oportunidades/interest/${matchId}/reject`);
      toast.success('Match rechazado');
      loadOportunidades();
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  const handleCloseDeal = async (match) => {
    if (!window.confirm(`¿Cerrar este deal? Se calculará 5% de comisión sobre $${match.precio?.toLocaleString() || 0}`)) return;
    try {
      await axios.put(`${API}/oportunidades/interest/${match.id}/close`);
      toast.success('Deal cerrado - Comisión calculada');
      loadOportunidades();
    } catch (error) {
      toast.error('Error al cerrar deal');
    }
  };

  const viewOportunidadDetail = (listing, type) => {
    setSelectedOportunidadDetail(listing);
    setOportunidadDetailType(type);
  };

  const handleCreateMatch = async (tengo, necesito) => {
    try {
      // Create a match
      await axios.post(`${API}/oportunidades/matches`, {
        tengo_id: tengo.id,
        necesito_id: necesito.id,
        tengo_user_name: tengo.user_name,
        necesito_user_name: necesito.user_name,
        material: tengo.material,
        cantidad: Math.min(tengo.cantidad, necesito.cantidad)
      });
      toast.success('Match creado');
      loadOportunidades();
    } catch (error) {
      toast.error('Error al crear match');
    }
  };

  const openWhatsApp = (phone, material) => {
    if (!phone) {
      toast.error('No hay teléfono registrado');
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Hola, vi tu oferta de ${material} en GenerIA Oportunidades, me interesa contactarte.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setShowContractWizard(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  // Abrir la vista de contrato (hub CAPTURAR / SÁBANA / GENERADORES / PRESUPUESTO / OPORTUNIDADES) como Super Admin
  const openContractHub = (project) => {
    setSelectedProject(project);
    setSelectedConcept(null);
    navigate('/contrato', { state: { fromAdmin: true } });
  };

  // Manual concept handlers
  const openManualConceptModal = (projectId) => {
    setManualConceptProjectId(projectId);
    setEditingConcept(null);
    setManualConceptData({
      clave: '',
      descripcion: '',
      unidad: 'm²',
      cantidad_total: '',
      precio_unitario: '',
      area: '',
      partida: '',
      cd_mo: '',
      cd_materiales: '',
      cd_total_manual: '',
      pu_mo: '',
      pu_con_material: ''
    });
    setShowManualConceptModal(true);
  };

  const openEditConceptModal = (concept) => {
    setManualConceptProjectId(concept.project_id);
    setEditingConcept(concept);
    setManualConceptData({
      clave: concept.clave || '',
      descripcion: concept.descripcion || '',
      unidad: concept.unidad || 'm²',
      cantidad_total: concept.contratado != null ? String(concept.contratado) : '',
      precio_unitario: concept.precio_unitario != null ? String(concept.precio_unitario) : '',
      area: concept.area || '',
      partida: concept.partida || '',
      cd_mo: concept.cd_mo != null ? String(concept.cd_mo) : '',
      cd_materiales: concept.cd_materiales != null ? String(concept.cd_materiales) : '',
      cd_total_manual: concept.cd_total_manual != null ? String(concept.cd_total_manual) : '',
      pu_mo: concept.pu_mo != null ? String(concept.pu_mo) : '',
      pu_con_material: concept.pu_con_material != null ? String(concept.pu_con_material) : ''
    });
    setShowManualConceptModal(true);
  };

  // Importe en vivo: cantidad × PU
  const importePreview = (parseFloat(manualConceptData.cantidad_total) || 0) * (parseFloat(manualConceptData.precio_unitario) || 0);
  // Precios opcionales: '' → null (campo no capturado)
  const optNum = (v) => (v === '' || v === null || v === undefined ? null : (Number.isFinite(parseFloat(v)) ? parseFloat(v) : null));
  const cdDesglosado = optNum(manualConceptData.cd_mo) !== null || optNum(manualConceptData.cd_materiales) !== null;
  const cdTotalPreview = cdDesglosado
    ? (optNum(manualConceptData.cd_mo) || 0) + (optNum(manualConceptData.cd_materiales) || 0)
    : optNum(manualConceptData.cd_total_manual);

  const handleSaveManualConcept = async () => {
    if (!manualConceptData.clave.trim() || !manualConceptData.descripcion.trim()) {
      toast.error('Clave y descripción son requeridos');
      return;
    }
    
    setSavingConcept(true);
    try {
      const payload = {
        clave: manualConceptData.clave.trim().toUpperCase(),
        descripcion: manualConceptData.descripcion.trim(),
        unidad: manualConceptData.unidad,
        contratado: manualConceptData.cantidad_total ? parseFloat(manualConceptData.cantidad_total) : 0,
        precio_unitario: manualConceptData.precio_unitario ? parseFloat(manualConceptData.precio_unitario) : 0,
        area: (manualConceptData.area || '').trim(),
        partida: (manualConceptData.partida || '').trim(),
        cd_mo: optNum(manualConceptData.cd_mo),
        cd_materiales: optNum(manualConceptData.cd_materiales),
        cd_total_manual: cdDesglosado ? null : optNum(manualConceptData.cd_total_manual),
        pu_mo: optNum(manualConceptData.pu_mo),
        pu_con_material: optNum(manualConceptData.pu_con_material)
      };

      if (editingConcept?.local) {
        // Concepto aún no sincronizado: actualizar el registro local
        const record = await offlineDb.getConcept(editingConcept.id);
        if (record) {
          await offlineDb.putConcept({ ...record, payload: { ...record.payload, ...payload } });
        }
        syncManager.notifyConceptsChanged();
        toast.success('Concepto actualizado en el dispositivo (se sincronizará)');
      } else if (editingConcept) {
        await axios.put(`${API}/concepts/${editingConcept.id}`, payload);
        toast.success('Concepto actualizado exitosamente');
      } else {
        // Crear: en línea → backend; sin conexión o red inestable → IndexedDB (mismo patrón que capturas)
        const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const created_at = new Date().toISOString();
        const fullPayload = { id, project_id: manualConceptProjectId, ...payload };
        let savedLocally = false;

        if (navigator.onLine) {
          try {
            await postWithTimeout(`${API}/concepts`, fullPayload);
          } catch (error) {
            if (!shouldQueue(error)) throw error;
            savedLocally = true;
          }
        } else {
          savedLocally = true;
        }

        if (savedLocally) {
          await offlineDb.putConcept({ id, project_id: manualConceptProjectId, payload: fullPayload, created_at });
          syncManager.refreshPending();
          toast.success('Concepto guardado en el dispositivo (se sincronizará)');
        } else {
          toast.success('Concepto agregado exitosamente');
        }
        syncManager.notifyConceptsChanged();
      }
      
      setShowManualConceptModal(false);
      setEditingConcept(null);
      setConceptsRefreshKey(k => k + 1);
      if (navigator.onLine) loadData(); // Refresh to show updated concept count
    } catch (error) {
      console.error('Error saving concept:', error);
      toast.error(editingConcept ? 'Error al actualizar el concepto' : 'Error al guardar el concepto');
    } finally {
      setSavingConcept(false);
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.numero_contrato?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Administrador';
      case 'contractor': return 'Contratista';
      case 'inspector': return 'Residente';
      default: return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-[#A855F7]/20 text-[#A855F7]';
      case 'contractor': return 'bg-[#FF6B00]/20 text-[#FF6B00]';
      case 'inspector': return 'bg-[#0066FF]/20 text-[#0066FF]';
      default: return 'bg-[#5A7A9A]/20 text-[#5A7A9A]';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTimeAgo = (hours) => {
    if (hours === null || hours === undefined) return null;
    if (hours < 1) return 'Hace menos de 1 hora';
    if (hours < 24) return `Hace ${Math.round(hours)} horas`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Hace 1 día';
    return `Hace ${days} días`;
  };

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={GENERIA_LOGO} alt="GenerIA" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="text-xl font-bold tracking-wide text-[#E2EAF4]">ADMIN DASHBOARD</h1>
                <p className="text-[#00CFFF] text-sm mono tagline">Panel de Administración</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-medium text-[#E2EAF4]">{user?.name}</p>
                <p className="text-[#8BA3C7] text-xs">Super Admin</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-[#E2EAF4] hover:bg-[#1E3550]"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-[#0D1520] border-b border-[#1E3550] sticky top-[72px] z-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('contratos')}
              className={`px-6 py-4 font-medium transition-colors relative ${
                activeTab === 'contratos' 
                  ? 'text-[#0066FF]' 
                  : 'text-[#8BA3C7] hover:text-[#E2EAF4]'
              }`}
              data-testid="tab-contratos"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                CONTRATOS
              </div>
              {activeTab === 'contratos' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0066FF]" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('usuarios')}
              className={`px-6 py-4 font-medium transition-colors relative ${
                activeTab === 'usuarios' 
                  ? 'text-[#0066FF]' 
                  : 'text-[#8BA3C7] hover:text-[#E2EAF4]'
              }`}
              data-testid="tab-usuarios"
            >
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                USUARIOS
              </div>
              {activeTab === 'usuarios' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0066FF]" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('intelligence')}
              className={`px-6 py-4 font-medium transition-colors relative ${
                activeTab === 'intelligence' 
                  ? 'text-[#A855F7]' 
                  : 'text-[#8BA3C7] hover:text-[#E2EAF4]'
              }`}
              data-testid="tab-intelligence"
            >
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                INTELLIGENCE
              </div>
              {activeTab === 'intelligence' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#A855F7]" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('oportunidades')}
              className={`px-6 py-4 font-medium transition-colors relative ${
                activeTab === 'oportunidades' 
                  ? 'text-[#00CFFF]' 
                  : 'text-[#8BA3C7] hover:text-[#E2EAF4]'
              }`}
              data-testid="tab-oportunidades"
            >
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                OPORTUNIDADES
              </div>
              {activeTab === 'oportunidades' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00CFFF]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* KPIs Row - Only show on Contratos tab */}
        {activeTab === 'contratos' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="kpis-row">
            {/* KPI 1: Active Contracts */}
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#0066FF]/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#0066FF]" />
                </div>
                <span className="text-sm text-[#5A7A9A]">Contratos Activos</span>
              </div>
              <p className="text-3xl font-bold text-[#E2EAF4]" data-testid="kpi-active-contracts">
                {kpis.active_contracts}
              </p>
            </div>
            
            {/* KPI 2: Captures This Week */}
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-[#22C55E]" />
                </div>
                <span className="text-sm text-[#5A7A9A]">Capturas Esta Semana</span>
              </div>
              <p className="text-3xl font-bold text-[#E2EAF4]" data-testid="kpi-captures-week">
                {kpis.captures_this_week}
              </p>
            </div>
            
            {/* KPI 3: Active Residentes */}
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#00CFFF]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#00CFFF]" />
                </div>
                <span className="text-sm text-[#5A7A9A]">Residentes Activos</span>
              </div>
              <p className="text-3xl font-bold text-[#E2EAF4]" data-testid="kpi-active-residentes">
                {kpis.active_residentes}
              </p>
            </div>
            
            {/* KPI 4: Closed Weeks */}
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#A855F7]/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#A855F7]" />
                </div>
                <span className="text-sm text-[#5A7A9A]">Semanas Cerradas</span>
              </div>
              <p className="text-3xl font-bold text-[#E2EAF4]" data-testid="kpi-closed-weeks">
                {kpis.closed_weeks}
              </p>
            </div>
          </div>
        )}

        {/* Search and Actions - Only for contratos and usuarios tabs */}
        {(activeTab === 'contratos' || activeTab === 'usuarios') && (
          <div className="flex items-center justify-between mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8BA3C7]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={activeTab === 'contratos' ? 'Buscar contratos...' : 'Buscar usuarios...'}
                className="pl-10 bg-[#0D1520] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#4A6B8A]"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={loadData} disabled={loading} className="text-[#8BA3C7] hover:text-[#E2EAF4] hover:bg-[#1E3550]">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              
              {activeTab === 'contratos' ? (
                <Button 
                  onClick={() => {
                    setEditingContract(null);
                    setShowContractWizard(true);
                  }}
                  className="bg-[#0066FF] hover:bg-[#0052CC]"
                  data-testid="btn-new-contract"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Contrato
                </Button>
              ) : (
                <Button 
                  onClick={() => {
                    setEditingUser(null);
                    setShowUserModal(true);
                  }}
                  className="bg-[#0066FF] hover:bg-[#0052CC]"
                  data-testid="btn-new-user"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Usuario
                </Button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-[#5A7A9A] animate-spin" />
          </div>
        ) : activeTab === 'contratos' ? (
          /* CONTRACTS CARDS */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="contracts-grid">
            {filteredProjects.length === 0 ? (
              <div className="col-span-2 bg-[#0D1520] rounded-xl border border-[#1E3550] p-12 text-center text-[#5A7A9A]">
                No hay contratos registrados
              </div>
            ) : (
              filteredProjects.map((project) => {
                const hasAlert = project.hours_since_capture && project.hours_since_capture > 72; // More than 3 days
                const timeAgo = formatTimeAgo(project.hours_since_capture);
                
                return (
                  <div 
                    key={project.id}
                    className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden hover:border-[#0066FF]/50 transition-colors"
                    data-testid={`contract-card-${project.id}`}
                  >
                    {/* Card Header */}
                    <div className="p-4 border-b border-[#1E3550]">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-[#E2EAF4] mb-1">{project.name}</h3>
                          <p className="text-sm text-[#5A7A9A]">{project.numero_contrato || 'Sin número de contrato'}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          project.status === 'active' 
                            ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                            : 'bg-[#5A7A9A]/20 text-[#5A7A9A]'
                        }`}>
                          {project.status === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Card Body */}
                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Circular Progress Arc */}
                        <div className="flex-shrink-0 relative" style={{ width: '80px', height: '80px' }}>
                          <svg width="80" height="80" viewBox="0 0 80 80" className="transform -rotate-90">
                            {/* Background arc (270° = 0.75 of circle) */}
                            <circle
                              cx="40"
                              cy="40"
                              r="32"
                              fill="none"
                              stroke="#1E3550"
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={`${32 * 2 * Math.PI * 0.75} ${32 * 2 * Math.PI * 0.25}`}
                              strokeDashoffset={32 * 2 * Math.PI * 0.125}
                            />
                            {/* Progress arc */}
                            <circle
                              cx="40"
                              cy="40"
                              r="32"
                              fill="none"
                              stroke="#0066FF"
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={`${32 * 2 * Math.PI * 0.75 * Math.min(project.progress, 100) / 100} ${32 * 2 * Math.PI}`}
                              strokeDashoffset={32 * 2 * Math.PI * 0.125}
                              style={{ transition: 'stroke-dasharray 0.5s ease' }}
                            />
                          </svg>
                          {/* Center text overlay */}
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-lg font-bold text-[#E2EAF4]">{project.progress?.toFixed(0)}%</span>
                            <span className="text-[10px] text-[#5A7A9A] uppercase tracking-wide">avance</span>
                          </div>
                        </div>
                        
                        {/* Info Column */}
                        <div className="flex-1 space-y-2">
                          {/* Info Row */}
                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            {project.current_week && (
                              <div className="flex items-center gap-1.5 text-[#00CFFF]">
                                <Calendar className="w-4 h-4" />
                                <span className="font-medium">SEM.{project.current_week}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 text-[#5A7A9A]">
                              <FileText className="w-4 h-4" />
                              <span>{project.concept_count} conceptos</span>
                            </div>
                            {project.closed_weeks > 0 && (
                              <div className="flex items-center gap-1.5 text-[#A855F7]">
                                <CheckCircle2 className="w-4 h-4" />
                                <span>{project.closed_weeks} cerradas</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Importe Ejecutado (MXN) vs Importe contratado */}
                          {(project.importe_contratado > 0 || project.importe_ejecutado > 0) && (
                            <div className="flex items-center gap-2 text-sm" data-testid={`card-importe-${project.id}`}>
                              <DollarSign className="w-4 h-4 text-[#00CFFF]" />
                              <span className="font-mono font-semibold text-[#00CFFF]" data-testid={`card-importe-ejecutado-${project.id}`}>
                                {formatMXN(project.importe_ejecutado)}
                              </span>
                              <span className="text-[#5A7A9A]">
                                de <span className="font-mono">{formatMXN(project.importe_contratado)}</span>
                                {project.importe_contratado > 0 && (
                                  <> · {((project.importe_ejecutado / project.importe_contratado) * 100).toFixed(1)}%</>
                                )}
                              </span>
                            </div>
                          )}
                          
                          {/* Last Capture */}
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-[#5A7A9A]" />
                            {timeAgo ? (
                              <span className="text-[#8BA3C7]">
                                {timeAgo}
                                {project.last_capture_inspector && (
                                  <span className="text-[#00CFFF]"> · {project.last_capture_inspector}</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-[#5A7A9A]">Sin capturas</span>
                            )}
                          </div>
                          
                          {/* Alert */}
                          {hasAlert && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg">
                              <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
                              <span className="text-sm text-[#EF4444]">Sin capturas hace más de 3 días</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Card Footer */}
                    <div className="px-4 py-3 bg-[#0A1422] border-t border-[#1E3550] flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 text-[#8BA3C7] hover:text-[#E2EAF4] hover:bg-[#1E3550]"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditContract(project);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Ver Contrato
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-[#00CFFF]/15 border border-[#00CFFF]/40 text-[#00CFFF] hover:bg-[#00CFFF]/25"
                        onClick={(e) => {
                          e.stopPropagation();
                          openContractHub(project);
                        }}
                        title="Abrir la vista del contrato (CAPTURAR / SÁBANA / GENERADORES / PRESUPUESTO / OPORTUNIDADES)"
                        data-testid={`btn-open-hub-${project.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Abrir
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-[#0066FF] hover:bg-[#0052CC]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProject(project);
                          setSelectedConcept(null);
                          navigate('/generadores', { state: { fromAdmin: true } });
                        }}
                      >
                        <Table className="w-4 h-4 mr-2" />
                        Ver Generadores
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : activeTab === 'usuarios' ? (
          /* USERS LIST */
          <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#0A1422] border-b border-[#1E3550]">
                <tr>
                  <th className="text-left px-6 py-4 font-semibold text-[#5A7A9A]">Usuario</th>
                  <th className="text-left px-4 py-4 font-semibold text-[#5A7A9A]">Rol</th>
                  <th className="text-left px-4 py-4 font-semibold text-[#5A7A9A]">Contratos Asignados</th>
                  <th className="text-left px-4 py-4 font-semibold text-[#5A7A9A]">Estado</th>
                  <th className="text-center px-4 py-4 font-semibold text-[#5A7A9A]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-[#5A7A9A]">
                      No hay usuarios registrados
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u, idx) => (
                    <tr 
                      key={u.id} 
                      className={`border-b border-[#1E3550] hover:bg-[#1E3550]/50 cursor-pointer ${idx % 2 === 0 ? 'bg-[#0D1520]' : 'bg-[#0A1422]'}`}
                      onClick={() => handleEditUser(u)}
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#1E3550] rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-[#5A7A9A]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#E2EAF4]">{u.name}</p>
                            <p className="text-sm text-[#5A7A9A]">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                          {getRoleLabel(u.role)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {u.assigned_projects?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {u.assigned_projects.slice(0, 2).map(pid => {
                              const project = projects.find(p => p.id === pid);
                              return project ? (
                                <span key={pid} className="px-2 py-1 bg-[#1E3550] text-[#E2EAF4] rounded text-xs">
                                  {project.name.substring(0, 15)}...
                                </span>
                              ) : null;
                            })}
                            {u.assigned_projects.length > 2 && (
                              <span className="px-2 py-1 bg-[#1E3550] text-[#5A7A9A] rounded text-xs">
                                +{u.assigned_projects.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[#5A7A9A] text-sm">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          u.status === 'active' 
                            ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                            : 'bg-[#EF4444]/20 text-[#EF4444]'
                        }`}>
                          {u.status === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-[#5A7A9A] hover:text-[#E2EAF4] hover:bg-[#1E3550]"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditUser(u);
                          }}
                          disabled={u.role === 'super_admin'}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Intelligence Tab */}
        {activeTab === 'intelligence' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-[#E2EAF4] flex items-center gap-3">
                  <Brain className="w-7 h-7 text-[#A855F7]" />
                  GenerIA Intelligence
                </h2>
                <p className="text-[#5A7A9A] mt-1">GenerIA vs Residentes - Análisis de Precisión</p>
              </div>
              <Button
                onClick={loadIntelligence}
                disabled={loadingIntelligence}
                className="bg-[#A855F7] hover:bg-[#9333EA] text-white"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingIntelligence ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>

            {/* Stats Cards */}
            {intelligenceStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total Analyzed */}
                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#A855F7]/20 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-[#A855F7]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Figuras Analizadas</span>
                  </div>
                  <p className="text-3xl font-bold text-[#E2EAF4]">
                    {intelligenceStats.total_analyzed}
                  </p>
                </div>

                {/* Average Precision */}
                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
                      <Target className="w-5 h-5 text-[#22C55E]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Precisión Promedio</span>
                  </div>
                  <p className="text-3xl font-bold text-[#E2EAF4]">
                    {intelligenceStats.average_precision ? `${intelligenceStats.average_precision}%` : '-'}
                  </p>
                </div>

                {/* Best Figure Type */}
                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#0066FF]/20 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-[#0066FF]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Mejor Tipo</span>
                  </div>
                  <p className="text-xl font-bold text-[#E2EAF4] capitalize">
                    {intelligenceStats.best_figure_type?.type || '-'}
                  </p>
                  {intelligenceStats.best_figure_type && (
                    <p className="text-sm text-[#22C55E]">{intelligenceStats.best_figure_type.precision}%</p>
                  )}
                </div>

                {/* Worst Figure Type */}
                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#FF6B00]/20 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-[#FF6B00]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Peor Tipo</span>
                  </div>
                  <p className="text-xl font-bold text-[#E2EAF4] capitalize">
                    {intelligenceStats.worst_figure_type?.type || '-'}
                  </p>
                  {intelligenceStats.worst_figure_type && (
                    <p className="text-sm text-[#FF6B00]">{intelligenceStats.worst_figure_type.precision}%</p>
                  )}
                </div>
              </div>
            )}

            {/* Figure Predictions Section (Vision vs Residente - Figures) */}
            {figurePredictionStats && (
              <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden">
                <div className="px-6 py-4 border-b border-[#1E3550] bg-gradient-to-r from-[#040810] to-[#060C18]">
                  <h3 className="text-lg font-semibold text-[#E2EAF4] flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[#A855F7]" />
                    Vision vs Residente — Figuras
                  </h3>
                  <p className="text-sm text-[#5A7A9A] mt-1">
                    Predicción silenciosa de tipo de figura y posición antes del dibujo
                  </p>
                </div>
                
                {/* Figure Prediction Stats */}
                <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-4 border-b border-[#1E3550]">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#E2EAF4]">{figurePredictionStats.total_predictions}</p>
                    <p className="text-xs text-[#5A7A9A]">Total Figuras</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#22C55E]">{figurePredictionStats.matches}</p>
                    <p className="text-xs text-[#5A7A9A]">Coincidencias</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#EF4444]">{figurePredictionStats.mismatches}</p>
                    <p className="text-xs text-[#5A7A9A]">Diferencias</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#00CFFF]">{figurePredictionStats.match_rate}%</p>
                    <p className="text-xs text-[#5A7A9A]">Tasa de Acierto</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#A855F7] capitalize">{figurePredictionStats.most_proposed_type || '-'}</p>
                    <p className="text-xs text-[#5A7A9A]">Más Propuesto</p>
                  </div>
                </div>
                
                {/* Figure Prediction Results */}
                {figurePredictions.length === 0 ? (
                  <div className="p-8 text-center">
                    <Layers className="w-10 h-10 text-[#5A7A9A] mx-auto mb-2 opacity-50" />
                    <p className="text-[#5A7A9A]">No hay predicciones de figuras registradas aún</p>
                    <p className="text-xs text-[#5A7A9A] mt-1">Las predicciones se generan cuando los residentes cargan fotos</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#1E3550] max-h-80 overflow-y-auto">
                    {figurePredictions.map((pred) => (
                      <div 
                        key={pred.id} 
                        className="p-3 hover:bg-[#0A1422] transition-colors cursor-pointer"
                        onClick={async () => {
                          setLoadingFigureDetail(true);
                          setShowFigurePredictionDetail(true);
                          setVisionImageDims({ width: 0, height: 0 });
                          setResidenteImageDims({ width: 0, height: 0 });
                          try {
                            const res = await axios.get(`${API}/admin/intelligence/figure-prediction/${pred.id}`);
                            setSelectedFigurePrediction(res.data);
                          } catch (err) {
                            console.error('Error fetching prediction detail:', err);
                            setSelectedFigurePrediction(pred); // Fallback to list data
                          } finally {
                            setLoadingFigureDetail(false);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {/* Match indicator */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              pred.figura_match ? 'bg-[#22C55E]/20' : 'bg-[#EF4444]/20'
                            }`}>
                              {pred.figura_match ? (
                                <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                              ) : (
                                <XCircle className="w-5 h-5 text-[#EF4444]" />
                              )}
                            </div>
                            
                            <div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-[#5A7A9A]">Vision:</span>
                                <span className="px-2 py-0.5 rounded bg-[#06B6D4]/20 text-[#06B6D4] text-xs font-medium capitalize">
                                  {pred.figura_type || '-'}
                                </span>
                                <span className="text-[#5A7A9A]">→</span>
                                <span className="text-[#5A7A9A]">Residente:</span>
                                <span className="px-2 py-0.5 rounded bg-[#FF6B00]/20 text-[#FF6B00] text-xs font-medium capitalize">
                                  {pred.residente_figura_type || (typeof pred.residente_figura === 'object' ? pred.residente_figura?.figura_type : pred.residente_figura) || '-'}
                                </span>
                              </div>
                              <p className="text-xs text-[#5A7A9A] mt-1">
                                {pred.concept_clave && <span className="text-[#00CFFF]">{pred.concept_clave}</span>}
                                {pred.concept_name && <span className="ml-2">{pred.concept_name.substring(0, 40)}...</span>}
                                <span className="ml-2 text-[#5A7A9A]">({pred.unit})</span>
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className="text-right text-xs text-[#5A7A9A]">
                              {pred.timestamp && new Date(pred.timestamp).toLocaleDateString('es-MX', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <Eye className="w-4 h-4 text-[#5A7A9A]" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Measurement Predictions Section (Vision vs Residente - Mediciones) */}
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#1E3550] bg-gradient-to-r from-[#040810] to-[#060C18]">
                <h3 className="text-lg font-semibold text-[#E2EAF4] flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-[#FF6B00]" />
                  Vision vs Residente — Mediciones
                </h3>
                <p className="text-sm text-[#5A7A9A] mt-1">
                  Predicción de largo × alto antes de que residente ingrese medidas
                </p>
              </div>
              
              {/* Measurement Stats */}
              {measurementPredictionStats && (
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-[#1E3550]">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#E2EAF4]">{measurementPredictionStats.total_predictions}</p>
                    <p className="text-xs text-[#5A7A9A]">Total Mediciones</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#22C55E]">
                      {measurementPredictionStats.average_precision_pct != null 
                        ? `${measurementPredictionStats.average_precision_pct}%` 
                        : '-'}
                    </p>
                    <p className="text-xs text-[#5A7A9A]">Precisión Promedio</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-[#00CFFF] truncate" title={measurementPredictionStats.best_concept || '-'}>
                      {measurementPredictionStats.best_concept 
                        ? (measurementPredictionStats.best_concept.length > 20 
                            ? measurementPredictionStats.best_concept.substring(0, 20) + '...' 
                            : measurementPredictionStats.best_concept)
                        : '-'}
                    </p>
                    <p className="text-xs text-[#5A7A9A]">Mejor Concepto</p>
                  </div>
                  <div className="text-center">
                    <div className="flex justify-center gap-1 text-xs">
                      {measurementPredictionStats.confidence_breakdown?.high && (
                        <span className="px-1.5 py-0.5 rounded bg-[#22C55E]/20 text-[#22C55E]">
                          Alta: {measurementPredictionStats.confidence_breakdown.high}
                        </span>
                      )}
                      {measurementPredictionStats.confidence_breakdown?.medium && (
                        <span className="px-1.5 py-0.5 rounded bg-[#FFB800]/20 text-[#FFB800]">
                          Media: {measurementPredictionStats.confidence_breakdown.medium}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#5A7A9A] mt-1">Confianza</p>
                  </div>
                </div>
              )}
              
              {/* Measurement Prediction Results */}
              {measurementPredictions.length === 0 ? (
                <div className="p-8 text-center">
                  <Ruler className="w-10 h-10 text-[#5A7A9A] mx-auto mb-2 opacity-50" />
                  <p className="text-[#5A7A9A]">No hay predicciones de mediciones registradas aún</p>
                  <p className="text-xs text-[#5A7A9A] mt-1">Las predicciones se generan al confirmar figuras</p>
                </div>
              ) : (
                <div className="divide-y divide-[#1E3550] max-h-80 overflow-y-auto">
                  {measurementPredictions.map((pred) => (
                    <div 
                      key={pred.id} 
                      className="p-3 hover:bg-[#0A1422] transition-colors cursor-pointer"
                      onClick={async () => {
                        setLoadingMeasurementDetail(true);
                        setShowMeasurementPredictionDetail(true);
                        setMeasurementVisionImageDims({ width: 0, height: 0 });
                        setMeasurementResidenteImageDims({ width: 0, height: 0 });
                        try {
                          const res = await axios.get(`${API}/admin/intelligence/measurement-prediction/${pred.id}`);
                          setSelectedMeasurementPrediction(res.data);
                        } catch (err) {
                          console.error('Error fetching measurement detail:', err);
                          setSelectedMeasurementPrediction(pred);
                        } finally {
                          setLoadingMeasurementDetail(false);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Photo number badge */}
                          {pred.photo_number && (
                            <span className="px-2 py-1 bg-[#FF6B00]/20 text-[#FF6B00] text-xs font-medium rounded flex-shrink-0">
                              Foto {String(pred.photo_number).padStart(3, '0')}
                            </span>
                          )}
                          
                          {/* Concept name */}
                          <span className="text-sm text-[#E2EAF4] truncate">
                            {pred.concept_name?.substring(0, 35) || '-'}
                            {pred.concept_name?.length > 35 ? '...' : ''}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 flex-shrink-0">
                          {/* Vision measurement */}
                          <div className="text-right">
                            <p className="text-xs text-[#06B6D4]">Vision</p>
                            <p className="text-sm font-medium text-[#E2EAF4]">
                              {pred.vision_largo_m != null && pred.vision_alto_m != null 
                                ? `${pred.vision_largo_m.toFixed(2)} × ${pred.vision_alto_m.toFixed(2)}` 
                                : '-'}
                            </p>
                          </div>
                          
                          {/* Residente measurement */}
                          <div className="text-right">
                            <p className="text-xs text-[#FF6B00]">Residente</p>
                            <p className="text-sm font-medium text-[#E2EAF4]">
                              {pred.residente_largo_m != null && pred.residente_alto_m != null 
                                ? `${pred.residente_largo_m.toFixed(2)} × ${pred.residente_alto_m.toFixed(2)}` 
                                : '-'}
                            </p>
                          </div>
                          
                          {/* Precision */}
                          <div className="text-right w-16">
                            <p className={`text-lg font-bold ${
                              pred.precision_overall_pct >= 90 ? 'text-[#22C55E]' :
                              pred.precision_overall_pct >= 70 ? 'text-[#FFB800]' :
                              'text-[#EF4444]'
                            }`}>
                              {pred.precision_overall_pct != null ? `${pred.precision_overall_pct.toFixed(0)}%` : '-'}
                            </p>
                          </div>
                          
                          {/* Confidence badge */}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                            pred.vision_confidence === 'high' ? 'bg-[#22C55E]/20 text-[#22C55E]' :
                            pred.vision_confidence === 'medium' ? 'bg-[#FFB800]/20 text-[#FFB800]' :
                            'bg-[#EF4444]/20 text-[#EF4444]'
                          }`}>
                            {pred.vision_confidence || '-'}
                          </span>
                          
                          <Eye className="w-4 h-4 text-[#5A7A9A]" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Results List */}
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#1E3550] bg-gradient-to-r from-[#040810] to-[#060C18]">
                <h3 className="text-lg font-semibold text-[#E2EAF4]">Resultados de Análisis</h3>
              </div>
              
              {loadingIntelligence ? (
                <div className="p-12 text-center">
                  <RefreshCw className="w-8 h-8 text-[#A855F7] animate-spin mx-auto mb-3" />
                  <p className="text-[#5A7A9A]">Cargando datos de inteligencia...</p>
                </div>
              ) : intelligenceResults.length === 0 ? (
                <div className="p-12 text-center">
                  <Brain className="w-12 h-12 text-[#5A7A9A] mx-auto mb-3 opacity-50" />
                  <p className="text-[#5A7A9A]">No hay análisis registrados aún</p>
                  <p className="text-sm text-[#5A7A9A] mt-1">Los análisis se generan automáticamente cuando los residentes guardan figuras</p>
                </div>
              ) : (
                <div className="divide-y divide-[#1E3550]">
                  {intelligenceResults.map((result) => (
                    <div key={result.id} className="p-4 hover:bg-[#0A1422] transition-colors">
                      <div className="flex items-start gap-4">
                        {/* Thumbnail */}
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#1E3550] flex-shrink-0">
                          {result.photo_thumbnail ? (
                            <img 
                              src={`data:image/jpeg;base64,${result.photo_thumbnail}`}
                              alt="Thumbnail"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Camera className="w-8 h-8 text-[#5A7A9A]" />
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="px-2 py-0.5 rounded bg-[#A855F7]/20 text-[#A855F7] text-xs font-medium capitalize">
                              {result.figura_type}
                            </span>
                            {result.concept_clave && (
                              <span className="text-xs text-[#5A7A9A]">{result.concept_clave}</span>
                            )}
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              result.status === 'completed' 
                                ? 'bg-[#22C55E]/20 text-[#22C55E]'
                                : result.status === 'error'
                                ? 'bg-[#EF4444]/20 text-[#EF4444]'
                                : 'bg-[#FF6B00]/20 text-[#FF6B00]'
                            }`}>
                              {result.status === 'completed' ? 'Completado' : result.status === 'error' ? 'Error' : 'Pendiente'}
                            </span>
                            {/* Photo Quality Badge */}
                            {result.photo_quality && (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                result.photo_quality === 'good' 
                                  ? 'bg-[#22C55E]/20 text-[#22C55E]'
                                  : result.photo_quality === 'overexposed'
                                  ? 'bg-[#FBBF24]/20 text-[#FBBF24]'
                                  : result.photo_quality === 'dark'
                                  ? 'bg-[#6B7280]/20 text-[#9CA3AF]'
                                  : 'bg-[#EF4444]/20 text-[#EF4444]'
                              }`}>
                                {result.photo_quality === 'good' ? '📸 Buena' :
                                 result.photo_quality === 'overexposed' ? '⚠️ Destello' :
                                 result.photo_quality === 'dark' ? '🌑 Oscura' :
                                 '⚠️ Baja calidad'}
                              </span>
                            )}
                            {/* Confidence Badge */}
                            {result.confidence && (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                result.confidence === 'high' 
                                  ? 'bg-[#22C55E]/10 text-[#22C55E]'
                                  : result.confidence === 'medium'
                                  ? 'bg-[#FBBF24]/10 text-[#FBBF24]'
                                  : 'bg-[#EF4444]/10 text-[#EF4444]'
                              }`}>
                                {result.confidence === 'high' ? '↑ Alta' :
                                 result.confidence === 'medium' ? '→ Media' :
                                 '↓ Baja'}
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4 mt-2">
                            {/* Residente Measurements */}
                            <div>
                              <p className="text-xs text-[#5A7A9A] mb-1">Residente capturó:</p>
                              <p className="text-sm text-[#E2EAF4] font-mono">
                                {result.residente_medidas ? Object.entries(result.residente_medidas).map(([k, v]) => 
                                  `${k.charAt(0).toUpperCase()}: ${parseFloat(v).toFixed(2)}m`
                                ).join(' · ') : '-'}
                              </p>
                            </div>

                            {/* Vision Measurements */}
                            <div>
                              <p className="text-xs text-[#5A7A9A] mb-1">GenerIA propuso:</p>
                              <p className="text-[10px] text-[#4A6B8A] mb-1">powered by Claude Vision</p>
                              <p className="text-sm text-[#00CFFF] font-mono">
                                {result.vision_medidas ? Object.entries(result.vision_medidas).map(([k, v]) => 
                                  v !== null ? `${k.charAt(0).toUpperCase()}: ${parseFloat(v).toFixed(2)}m` : null
                                ).filter(Boolean).join(' · ') || '-' : '-'}
                              </p>
                            </div>
                          </div>

                          {result.error_message && (
                            <p className="text-xs text-[#EF4444] mt-1">{result.error_message}</p>
                          )}
                        </div>

                        {/* Precision */}
                        <div className="text-right flex-shrink-0">
                          {result.precision_percentage !== null ? (
                            <>
                              <p className={`text-2xl font-bold ${
                                result.precision_percentage >= 95 ? 'text-[#22C55E]' :
                                result.precision_percentage >= 85 ? 'text-[#00CFFF]' :
                                result.precision_percentage >= 70 ? 'text-[#FF6B00]' :
                                'text-[#EF4444]'
                              }`}>
                                {result.precision_percentage.toFixed(1)}%
                              </p>
                              <p className="text-xs text-[#5A7A9A]">Precisión</p>
                            </>
                          ) : (
                            <p className="text-sm text-[#5A7A9A]">-</p>
                          )}
                          <p className="text-xs text-[#5A7A9A] mt-2">
                            {result.timestamp ? new Date(result.timestamp).toLocaleDateString('es-MX', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Oportunidades Tab */}
        {activeTab === 'oportunidades' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-[#E2EAF4] flex items-center gap-3">
                  <Package className="w-7 h-7 text-[#00CFFF]" />
                  GenerIA Oportunidades
                </h2>
                <p className="text-[#5A7A9A] mt-1">Marketplace de Materiales - Panel Admin</p>
              </div>
              <Button
                onClick={loadOportunidades}
                disabled={loadingOportunidades}
                className="bg-[#00CFFF] hover:bg-[#00B8E6] text-[#060C18]"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingOportunidades ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>

            {/* Stats Cards */}
            {oportunidadesStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-[#22C55E]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Tengo Activos</span>
                  </div>
                  <p className="text-3xl font-bold text-[#E2EAF4]">{oportunidadesStats.active_tengo}</p>
                </div>

                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#FBBF24]/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-[#FBBF24]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Pendientes</span>
                  </div>
                  <p className="text-3xl font-bold text-[#E2EAF4]">{oportunidadesStats.pending_tengo}</p>
                </div>

                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#0066FF]/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-[#0066FF]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Necesito Activos</span>
                  </div>
                  <p className="text-3xl font-bold text-[#E2EAF4]">{oportunidadesStats.active_necesito}</p>
                </div>

                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#A855F7]/20 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-[#A855F7]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Matches</span>
                  </div>
                  <p className="text-3xl font-bold text-[#E2EAF4]">{oportunidadesStats.approved_matches}</p>
                </div>

                <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-[#00CFFF]/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#00CFFF]" />
                    </div>
                    <span className="text-sm text-[#5A7A9A]">Comisión</span>
                  </div>
                  <p className="text-3xl font-bold text-[#E2EAF4]">${oportunidadesStats.total_commission?.toLocaleString() || 0}</p>
                </div>
              </div>
            )}

            {/* Sub-tabs */}
            <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] overflow-hidden">
              <div className="flex border-b border-[#1E3550]">
                <button
                  onClick={() => setOportunidadesSubTab('tengo')}
                  className={`flex-1 px-6 py-3 font-medium transition-colors ${
                    oportunidadesSubTab === 'tengo' 
                      ? 'bg-[#22C55E]/10 text-[#22C55E] border-b-2 border-[#22C55E]' 
                      : 'text-[#5A7A9A] hover:text-[#E2EAF4]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Truck className="w-4 h-4" />
                    TENGO ({oportunidadesTengo.length})
                  </div>
                </button>
                <button
                  onClick={() => setOportunidadesSubTab('necesito')}
                  className={`flex-1 px-6 py-3 font-medium transition-colors ${
                    oportunidadesSubTab === 'necesito' 
                      ? 'bg-[#0066FF]/10 text-[#0066FF] border-b-2 border-[#0066FF]' 
                      : 'text-[#5A7A9A] hover:text-[#E2EAF4]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Package className="w-4 h-4" />
                    NECESITO ({oportunidadesNecesito.length})
                  </div>
                </button>
                <button
                  onClick={() => setOportunidadesSubTab('matches')}
                  className={`flex-1 px-6 py-3 font-medium transition-colors ${
                    oportunidadesSubTab === 'matches' 
                      ? 'bg-[#A855F7]/10 text-[#A855F7] border-b-2 border-[#A855F7]' 
                      : 'text-[#5A7A9A] hover:text-[#E2EAF4]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    MATCHES ({oportunidadesMatches.length})
                  </div>
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                {loadingOportunidades ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#00CFFF] animate-spin" />
                  </div>
                ) : oportunidadesSubTab === 'tengo' ? (
                  /* TENGO Listings */
                  <div className="space-y-3">
                    {oportunidadesTengo.length === 0 ? (
                      <p className="text-center text-[#5A7A9A] py-8">No hay ofertas registradas</p>
                    ) : (
                      oportunidadesTengo.map(listing => (
                        <div key={listing.id} className={`p-4 rounded-lg border ${
                          listing.status === 'pending' 
                            ? 'border-[#FBBF24] bg-[#FBBF24]/5' 
                            : listing.status === 'approved'
                            ? 'border-[#22C55E] bg-[#22C55E]/5'
                            : 'border-[#EF4444] bg-[#EF4444]/5'
                        }`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              {/* Photos preview */}
                              {listing.fotos && listing.fotos.length > 0 && (
                                <div className="flex gap-1 mb-2">
                                  {listing.fotos.slice(0, 3).map((foto, idx) => (
                                    <div key={idx} className="w-12 h-12 rounded overflow-hidden border border-[#1E3550]">
                                      <img src={foto} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold text-[#E2EAF4]">{listing.material}</h4>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  listing.status === 'pending' ? 'bg-[#FBBF24]/20 text-[#FBBF24]' :
                                  listing.status === 'approved' ? 'bg-[#22C55E]/20 text-[#22C55E]' :
                                  'bg-[#EF4444]/20 text-[#EF4444]'
                                }`}>
                                  {listing.status === 'pending' ? 'Pendiente' : 
                                   listing.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                </span>
                                {listing.condicion && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    listing.condicion === 'nuevo' ? 'bg-[#22C55E]/20 text-[#22C55E]' : 'bg-[#FF6B00]/20 text-[#FF6B00]'
                                  }`}>
                                    {listing.condicion === 'nuevo' ? 'Nuevo' : 'Sobrante'}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-3 text-sm mb-2">
                                <span className="text-[#00CFFF] font-mono">{listing.cantidad} {listing.unidad}</span>
                                <span className="text-[#5A7A9A] flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {listing.ubicacion}
                                </span>
                                {listing.precio && (
                                  <span className="text-[#22C55E]">${listing.precio.toLocaleString()}/{listing.unidad}</span>
                                )}
                              </div>
                              <p className="text-sm text-[#8BA3C7]">
                                <User className="w-3 h-3 inline mr-1" />
                                {listing.user_name}
                                {listing.user_phone && (
                                  <span className="ml-2 text-[#5A7A9A]">
                                    <Phone className="w-3 h-3 inline mr-1" />{listing.user_phone}
                                  </span>
                                )}
                              </p>
                              {listing.descripcion && (
                                <p className="text-xs text-[#5A7A9A] mt-1 line-clamp-2">{listing.descripcion}</p>
                              )}
                            </div>
                            
                            <div className="flex flex-col gap-2">
                              {/* Ver Detalle */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewOportunidadDetail(listing, 'tengo')}
                                className="border-[#00CFFF] text-[#00CFFF] hover:bg-[#00CFFF]/10"
                              >
                                <Eye className="w-4 h-4 mr-1" /> Ver
                              </Button>
                              
                              {/* Approve/Reject for pending */}
                              {listing.status === 'pending' && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveTengo(listing.id)}
                                    className="bg-[#22C55E] hover:bg-[#16A34A] px-2"
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectTengo(listing.id)}
                                    className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 px-2"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                              
                              {/* Delete button */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteTengo(listing.id)}
                                className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : oportunidadesSubTab === 'necesito' ? (
                  /* NECESITO Listings */
                  <div className="space-y-3">
                    {oportunidadesNecesito.length === 0 ? (
                      <p className="text-center text-[#5A7A9A] py-8">No hay solicitudes registradas</p>
                    ) : (
                      oportunidadesNecesito.map(listing => (
                        <div key={listing.id} className={`p-4 rounded-lg border ${
                          listing.urgente ? 'border-[#FF6B00] bg-[#FF6B00]/5' : 'border-[#1E3550] bg-[#0A1422]'
                        }`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold text-[#E2EAF4]">{listing.material}</h4>
                                {listing.urgente && (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#FF6B00]/20 text-[#FF6B00] flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> URGENTE
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-3 text-sm mb-2">
                                <span className="text-[#00CFFF] font-mono">{listing.cantidad} {listing.unidad}</span>
                                <span className="text-[#5A7A9A] flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {listing.ubicacion}
                                </span>
                                {listing.presupuesto && (
                                  <span className="text-[#22C55E]">Presup: ${listing.presupuesto.toLocaleString()}</span>
                                )}
                              </div>
                              <p className="text-sm text-[#8BA3C7]">
                                <User className="w-3 h-3 inline mr-1" />
                                {listing.user_name}
                                {listing.user_phone && (
                                  <span className="ml-2 text-[#5A7A9A]">
                                    <Phone className="w-3 h-3 inline mr-1" />{listing.user_phone}
                                  </span>
                                )}
                              </p>
                              {listing.descripcion && (
                                <p className="text-xs text-[#5A7A9A] mt-1 line-clamp-2">{listing.descripcion}</p>
                              )}
                            </div>
                            
                            <div className="flex flex-col gap-2">
                              {/* Ver Detalle */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewOportunidadDetail(listing, 'necesito')}
                                className="border-[#00CFFF] text-[#00CFFF] hover:bg-[#00CFFF]/10"
                              >
                                <Eye className="w-4 h-4 mr-1" /> Ver
                              </Button>
                              
                              {/* Delete button */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteNecesito(listing.id)}
                                className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  /* MATCHES / INTERESTS */
                  <div className="space-y-4">
                    {/* Interests (pending matches from buyers) */}
                    {oportunidadesInterests.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-[#5A7A9A] mb-2 flex items-center gap-2">
                          <Clock className="w-4 h-4" /> Intereses Pendientes ({oportunidadesInterests.length})
                        </h4>
                        <div className="space-y-3">
                          {oportunidadesInterests.map(interest => (
                            <div key={interest.id} className={`p-4 rounded-lg border ${
                              interest.status === 'pendiente' ? 'border-[#FBBF24] bg-[#FBBF24]/5' :
                              interest.status === 'aprobado' ? 'border-[#22C55E] bg-[#22C55E]/5' :
                              interest.status === 'cerrado' ? 'border-[#A855F7] bg-[#A855F7]/5' :
                              'border-[#EF4444] bg-[#EF4444]/5'
                            }`}>
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-bold text-[#E2EAF4]">{interest.material}</h4>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      interest.status === 'pendiente' ? 'bg-[#FBBF24]/20 text-[#FBBF24]' :
                                      interest.status === 'aprobado' ? 'bg-[#22C55E]/20 text-[#22C55E]' :
                                      interest.status === 'cerrado' ? 'bg-[#A855F7]/20 text-[#A855F7]' :
                                      'bg-[#EF4444]/20 text-[#EF4444]'
                                    }`}>
                                      {interest.status === 'pendiente' ? 'Pendiente' : 
                                       interest.status === 'aprobado' ? 'Aprobado' : 
                                       interest.status === 'cerrado' ? 'Cerrado' : 'Rechazado'}
                                    </span>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="bg-[#22C55E]/10 rounded p-2 border border-[#22C55E]/30">
                                      <p className="text-[#5A7A9A] text-xs mb-1">VENDEDOR</p>
                                      <p className="text-[#22C55E] font-medium">{interest.seller_name}</p>
                                    </div>
                                    <div className="bg-[#0066FF]/10 rounded p-2 border border-[#0066FF]/30">
                                      <p className="text-[#5A7A9A] text-xs mb-1">COMPRADOR</p>
                                      <p className="text-[#0066FF] font-medium">{interest.buyer_name}</p>
                                      <p className="text-[#5A7A9A] text-xs">{interest.buyer_whatsapp}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="flex flex-wrap gap-3 text-sm mt-2">
                                    <span className="text-[#5A7A9A] flex items-center gap-1">
                                      <MapPin className="w-3 h-3" /> {interest.zona}
                                    </span>
                                    {interest.precio > 0 && (
                                      <span className="text-[#22C55E] flex items-center gap-1">
                                        <DollarSign className="w-3 h-3" /> ${interest.precio.toLocaleString()}
                                      </span>
                                    )}
                                    {interest.commission > 0 && (
                                      <span className="text-[#A855F7] flex items-center gap-1">
                                        💰 Comisión: ${interest.commission.toLocaleString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex flex-col gap-2">
                                  {interest.status === 'pendiente' && (
                                    <>
                                      <Button
                                        size="sm"
                                        onClick={() => handleApproveMatch(interest)}
                                        className="bg-[#22C55E] hover:bg-[#16A34A]"
                                      >
                                        <Check className="w-4 h-4 mr-1" /> Aprobar
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleRejectMatch(interest.id)}
                                        className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                                      >
                                        <X className="w-4 h-4 mr-1" /> Rechazar
                                      </Button>
                                    </>
                                  )}
                                  {interest.status === 'aprobado' && (
                                    <Button
                                      size="sm"
                                      onClick={() => handleCloseDeal(interest)}
                                      className="bg-[#A855F7] hover:bg-[#9333EA]"
                                    >
                                      💰 Cerrar Deal
                                    </Button>
                                  )}
                                  {interest.status === 'cerrado' && (
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-[#A855F7]">${(interest.commission || 0).toLocaleString()}</p>
                                      <p className="text-xs text-[#5A7A9A]">Comisión 5%</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Manual matches */}
                    {oportunidadesMatches.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-[#5A7A9A] mb-2 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" /> Matches Manuales ({oportunidadesMatches.length})
                        </h4>
                        <div className="space-y-3">
                          {oportunidadesMatches.map(match => (
                            <div key={match.id} className={`p-4 rounded-lg border ${
                              match.status === 'approved' ? 'border-[#22C55E] bg-[#22C55E]/5' :
                              match.status === 'completed' ? 'border-[#A855F7] bg-[#A855F7]/5' :
                              'border-[#1E3550] bg-[#0A1422]'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-bold text-[#E2EAF4]">{match.material}</h4>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      match.status === 'pending' ? 'bg-[#FBBF24]/20 text-[#FBBF24]' :
                                      match.status === 'approved' ? 'bg-[#22C55E]/20 text-[#22C55E]' :
                                      'bg-[#A855F7]/20 text-[#A855F7]'
                                    }`}>
                                      {match.status === 'pending' ? 'Pendiente' : 
                                       match.status === 'approved' ? 'Aprobado' : 'Completado'}
                                    </span>
                                  </div>
                                  <div className="text-sm text-[#5A7A9A]">
                                    <span className="text-[#22C55E]">{match.tengo_user_name}</span>
                                    <span className="mx-2">→</span>
                                    <span className="text-[#0066FF]">{match.necesito_user_name}</span>
                                    <span className="ml-3 text-[#00CFFF] font-mono">{match.cantidad} unid.</span>
                                  </div>
                                </div>
                                {match.commission > 0 && (
                                  <div className="text-right">
                                    <p className="text-lg font-bold text-[#00CFFF]">${match.commission.toLocaleString()}</p>
                                    <p className="text-xs text-[#5A7A9A]">Comisión</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {oportunidadesInterests.length === 0 && oportunidadesMatches.length === 0 && (
                      <p className="text-center text-[#5A7A9A] py-8">No hay matches registrados</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {showContractWizard && (
        <ContractWizardModal
          onClose={() => {
            setShowContractWizard(false);
            setEditingContract(null);
          }}
          onSuccess={loadData}
          users={users}
          editContract={editingContract}
          onOpenManualConcept={openManualConceptModal}
          onEditConcept={openEditConceptModal}
          conceptsRefreshKey={conceptsRefreshKey}
          onOpenHub={openContractHub}
        />
      )}

      {showUserModal && (
        <UserModal
          onClose={() => {
            setShowUserModal(false);
            setEditingUser(null);
          }}
          onSuccess={loadData}
          projects={projects}
          editUser={editingUser}
        />
      )}

      {/* Manual Concept Modal */}
      {showManualConceptModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0D1520] border border-[#1E3550] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-4 border-b border-[#1E3550] flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#E2EAF4]" data-testid="concept-modal-title">
                  {editingConcept ? 'Editar Concepto' : 'Agregar Concepto Manual'}
                </h2>
                <p className="text-sm text-[#5A7A9A]">
                  {editingConcept ? `Actualiza los datos de ${editingConcept.clave}` : 'Agrega un concepto al catálogo'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowManualConceptModal(false)}
                className="text-[#5A7A9A] hover:text-[#E2EAF4]"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Clave */}
              <div>
                <label className="text-sm font-medium text-[#E2EAF4] block mb-2">
                  Clave <span className="text-red-500">*</span>
                </label>
                <Input
                  value={manualConceptData.clave}
                  onChange={(e) => setManualConceptData(prev => ({ ...prev, clave: e.target.value }))}
                  placeholder="Ej: AF13DC"
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] uppercase"
                  data-testid="input-concept-clave"
                />
              </div>
              
              {/* Descripción */}
              <div>
                <label className="text-sm font-medium text-[#E2EAF4] block mb-2">
                  Descripción <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={manualConceptData.descripcion}
                  onChange={(e) => setManualConceptData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción completa del concepto"
                  className="w-full bg-[#0A1422] border border-[#1E3550] text-[#E2EAF4] rounded-md p-3 text-sm min-h-[80px] placeholder:text-[#4A6B8A]"
                  data-testid="input-concept-descripcion"
                />
              </div>
              
              {/* Área / Partida (opcionales) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#5A7A9A] mb-1 block">Área <span className="text-[#5A7A9A]/70">(opcional)</span></label>
                  <Input
                    value={manualConceptData.area}
                    onChange={(e) => setManualConceptData(prev => ({ ...prev, area: e.target.value }))}
                    placeholder="Ej: Casa Rosa, Bodega"
                    list="areas-existentes"
                    className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                    data-testid="input-concept-area"
                  />
                  <datalist id="areas-existentes">
                    {existingAreas.map((a) => <option key={a} value={a} />)}
                  </datalist>
                  {existingAreas.length > 0 && (
                    <p className="mt-1 text-[10px] text-[#5A7A9A]" data-testid="areas-hint">Usadas: {existingAreas.join(' · ')}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-[#5A7A9A] mb-1 block">Partida <span className="text-[#5A7A9A]/70">(opcional)</span></label>
                  <Input
                    value={manualConceptData.partida}
                    onChange={(e) => setManualConceptData(prev => ({ ...prev, partida: e.target.value }))}
                    placeholder="Ej: Albañilería, Instalaciones"
                    list="partidas-existentes"
                    className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                    data-testid="input-concept-partida"
                  />
                  <datalist id="partidas-existentes">
                    {existingPartidas.map((p) => <option key={p} value={p} />)}
                  </datalist>
                  {existingPartidas.length > 0 && (
                    <p className="mt-1 text-[10px] text-[#5A7A9A]" data-testid="partidas-hint">Usadas: {existingPartidas.join(' · ')}</p>
                  )}
                </div>
              </div>

              {/* Unidad */}
              <div>
                <label className="text-sm font-medium text-[#E2EAF4] block mb-2">Unidad</label>
                <select
                  value={manualConceptData.unidad}
                  onChange={(e) => setManualConceptData(prev => ({ ...prev, unidad: e.target.value }))}
                  className="w-full bg-[#0A1422] border border-[#1E3550] text-[#E2EAF4] rounded-md p-3 text-sm"
                  data-testid="select-concept-unidad"
                >
                  <option value="m²">m² (metros cuadrados)</option>
                  <option value="m³">m³ (metros cúbicos)</option>
                  <option value="ml">ml (metros lineales)</option>
                  <option value="pza">pza (piezas)</option>
                  <option value="kg">kg (kilogramos)</option>
                  <option value="ton">ton (toneladas)</option>
                  <option value="litro">litro</option>
                </select>
              </div>
              
              {/* Cantidad Total */}
              <div>
                <label className="text-sm font-medium text-[#E2EAF4] block mb-2">Cantidad Total</label>
                <Input
                  type="number"
                  step="0.01"
                  value={manualConceptData.cantidad_total}
                  onChange={(e) => setManualConceptData(prev => ({ ...prev, cantidad_total: e.target.value }))}
                  placeholder="Ej: 450.00"
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                  data-testid="input-concept-cantidad"
                />
              </div>
              
              {/* Precio Unitario (Opcional) */}
              <div>
                <label className="text-sm font-medium text-[#E2EAF4] block mb-2">
                  Precio Unitario <span className="text-[#5A7A9A]">(opcional)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={manualConceptData.precio_unitario}
                  onChange={(e) => setManualConceptData(prev => ({ ...prev, precio_unitario: e.target.value }))}
                  placeholder="Ej: 125.50"
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                  data-testid="input-concept-precio"
                />
              </div>

              {/* Costo Directo (interno) */}
              <div className="rounded-lg border border-[#0066FF]/25 bg-[#0066FF]/[0.06] p-4 space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#8FBBFF]">Costo Directo (interno · no aparece en el PDF del cliente)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#8FBBFF] mb-1 block">CD Mano de Obra <span className="opacity-60">(opcional)</span></label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualConceptData.cd_mo}
                      onChange={(e) => setManualConceptData(prev => ({ ...prev, cd_mo: e.target.value }))}
                      placeholder="Ej: 300.00"
                      className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                      data-testid="input-concept-cd-mo"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#8FBBFF] mb-1 block">CD Materiales <span className="opacity-60">(opcional)</span></label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualConceptData.cd_materiales}
                      onChange={(e) => setManualConceptData(prev => ({ ...prev, cd_materiales: e.target.value }))}
                      placeholder="Ej: 520.00"
                      className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                      data-testid="input-concept-cd-materiales"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#8FBBFF] mb-1 block">
                    CD Total {cdDesglosado ? <span className="opacity-60">(calculado: M.O. + Materiales)</span> : <span className="opacity-60">(combinado, opcional)</span>}
                  </label>
                  {cdDesglosado ? (
                    <p className="font-mono font-bold text-[#8FBBFF]" data-testid="concept-cd-total-preview">{formatMXN(cdTotalPreview || 0)}</p>
                  ) : (
                    <Input
                      type="number"
                      step="0.01"
                      value={manualConceptData.cd_total_manual}
                      onChange={(e) => setManualConceptData(prev => ({ ...prev, cd_total_manual: e.target.value }))}
                      placeholder="Ej: 820.00"
                      className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                      data-testid="input-concept-cd-total"
                    />
                  )}
                </div>
              </div>

              {/* PU Cliente (va al PDF) */}
              <div className="rounded-lg border border-[#0066FF]/40 bg-[#0066FF]/[0.14] p-4 space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#BFD8FF]">PU Cliente (estos precios sí aparecen en el PDF)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#BFD8FF] mb-1 block">PU Solo M.O. <span className="opacity-60">(opcional)</span></label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualConceptData.pu_mo}
                      onChange={(e) => setManualConceptData(prev => ({ ...prev, pu_mo: e.target.value }))}
                      placeholder="Ej: 450.00"
                      className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                      data-testid="input-concept-pu-mo"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#BFD8FF] mb-1 block">PU Con Material <span className="opacity-60">(opcional)</span></label>
                    <Input
                      type="number"
                      step="0.01"
                      value={manualConceptData.pu_con_material}
                      onChange={(e) => setManualConceptData(prev => ({ ...prev, pu_con_material: e.target.value }))}
                      placeholder="Ej: 1200.00"
                      className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                      data-testid="input-concept-pu-material"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <p className="text-[#DCEAFF]">
                    Utilidad Solo M.O.:{' '}
                    <span className="font-mono font-bold text-[#22C55E]" data-testid="concept-utilidad-mo-preview">
                      {optNum(manualConceptData.pu_mo) !== null && optNum(manualConceptData.cd_mo) !== null
                        ? formatMXN(optNum(manualConceptData.pu_mo) - optNum(manualConceptData.cd_mo))
                        : '—'}
                    </span>
                  </p>
                  <p className="text-[#DCEAFF]">
                    Utilidad Con Material:{' '}
                    <span className="font-mono font-bold text-[#22C55E]" data-testid="concept-utilidad-material-preview">
                      {optNum(manualConceptData.pu_con_material) !== null && cdTotalPreview !== null
                        ? formatMXN(optNum(manualConceptData.pu_con_material) - cdTotalPreview)
                        : '—'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Importe calculado en vivo: cantidad × PU */}
              <div className="flex items-center justify-between rounded-lg border border-[#1E3550] bg-[#0A1422] px-4 py-3">
                <div>
                  <span className="text-sm text-[#5A7A9A]">Importe</span>
                  <p className="text-[11px] text-[#5A7A9A]/70">Cantidad × Precio Unitario</p>
                </div>
                <span className="font-mono font-bold text-lg text-[#22C55E]" data-testid="concept-importe-preview">
                  {formatMXN(importePreview)}
                </span>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-[#1E3550] flex justify-end gap-3 bg-[#0A1422] rounded-b-2xl">
              <Button
                variant="ghost"
                onClick={() => setShowManualConceptModal(false)}
                className="text-[#E2EAF4] hover:bg-[#1E3550]"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveManualConcept}
                disabled={savingConcept || !manualConceptData.clave.trim() || !manualConceptData.descripcion.trim()}
                className="bg-[#A855F7] hover:bg-[#A855F7]/80 text-white"
                data-testid="btn-save-concept"
              >
                {savingConcept ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingConcept ? 'Guardar Cambios' : 'Guardar Concepto'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Oportunidad Detail Modal */}
      {selectedOportunidadDetail && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedOportunidadDetail(null)}>
          <div 
            className="bg-[#0D1520] rounded-2xl border border-[#1E3550] w-full max-w-lg max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-6 py-4 flex items-center justify-between border-b border-[#1E3550] ${
              oportunidadDetailType === 'tengo' 
                ? 'bg-gradient-to-r from-[#22C55E]/20 to-transparent' 
                : 'bg-gradient-to-r from-[#0066FF]/20 to-transparent'
            }`}>
              <div className="flex items-center gap-2">
                <Package className={`w-6 h-6 ${oportunidadDetailType === 'tengo' ? 'text-[#22C55E]' : 'text-[#0066FF]'}`} />
                <span className={`font-bold text-lg ${oportunidadDetailType === 'tengo' ? 'text-[#22C55E]' : 'text-[#0066FF]'}`}>
                  {oportunidadDetailType === 'tengo' ? 'TENGO' : 'NECESITO'}
                </span>
                {oportunidadDetailType === 'tengo' && selectedOportunidadDetail.condicion && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    selectedOportunidadDetail.condicion === 'nuevo' 
                      ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                      : 'bg-[#FF6B00]/20 text-[#FF6B00]'
                  }`}>
                    {selectedOportunidadDetail.condicion === 'nuevo' ? 'Nuevo' : 'Sobrante'}
                  </span>
                )}
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  selectedOportunidadDetail.status === 'pending' ? 'bg-[#FBBF24]/20 text-[#FBBF24]' :
                  selectedOportunidadDetail.status === 'approved' ? 'bg-[#22C55E]/20 text-[#22C55E]' :
                  'bg-[#EF4444]/20 text-[#EF4444]'
                }`}>
                  {selectedOportunidadDetail.status === 'pending' ? 'Pendiente' : 
                   selectedOportunidadDetail.status === 'approved' ? 'Aprobado' : 
                   selectedOportunidadDetail.status === 'active' ? 'Activo' : 'Rechazado'}
                </span>
              </div>
              <button onClick={() => setSelectedOportunidadDetail(null)} className="p-2 hover:bg-[#1E3550] rounded-lg text-[#E2EAF4]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              {/* Photos */}
              {selectedOportunidadDetail.fotos && selectedOportunidadDetail.fotos.length > 0 && (
                <div>
                  <p className="text-sm text-[#5A7A9A] mb-2">Fotos ({selectedOportunidadDetail.fotos.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedOportunidadDetail.fotos.map((foto, idx) => (
                      <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-[#1E3550]">
                        <img src={foto} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Material Name */}
              <h2 className="text-2xl font-bold text-[#E2EAF4]">{selectedOportunidadDetail.material}</h2>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0A1422] rounded-lg p-4 border border-[#1E3550]">
                  <div className="flex items-center gap-2 text-[#5A7A9A] mb-1">
                    <Tag className="w-4 h-4" />
                    <span className="text-sm">Cantidad</span>
                  </div>
                  <p className="text-xl font-bold text-[#00CFFF]">{selectedOportunidadDetail.cantidad} {selectedOportunidadDetail.unidad}</p>
                </div>
                
                <div className="bg-[#0A1422] rounded-lg p-4 border border-[#1E3550]">
                  <div className="flex items-center gap-2 text-[#5A7A9A] mb-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">Zona</span>
                  </div>
                  <p className="text-xl font-bold text-[#E2EAF4]">{selectedOportunidadDetail.ubicacion}</p>
                </div>
              </div>

              {/* Price */}
              {(selectedOportunidadDetail.precio || selectedOportunidadDetail.presupuesto) && (
                <div className="bg-[#22C55E]/10 rounded-lg p-4 border border-[#22C55E]/30">
                  <div className="flex items-center gap-2 text-[#22C55E] mb-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm">{oportunidadDetailType === 'tengo' ? 'Precio por unidad' : 'Presupuesto'}</span>
                  </div>
                  <p className="text-2xl font-bold text-[#22C55E]">
                    ${(selectedOportunidadDetail.precio || selectedOportunidadDetail.presupuesto).toLocaleString()} MXN
                  </p>
                </div>
              )}

              {/* Description */}
              {selectedOportunidadDetail.descripcion && (
                <div>
                  <p className="text-sm text-[#5A7A9A] mb-1">Descripción</p>
                  <p className="text-[#E2EAF4] bg-[#0A1422] rounded-lg p-3 border border-[#1E3550]">{selectedOportunidadDetail.descripcion}</p>
                </div>
              )}

              {/* User Info */}
              <div className="bg-[#0A1422] rounded-lg p-4 border border-[#1E3550]">
                <p className="text-sm text-[#5A7A9A] mb-2">Información del Usuario</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#5A7A9A]" />
                    <span className="text-[#E2EAF4] font-medium">{selectedOportunidadDetail.user_name}</span>
                  </div>
                  {selectedOportunidadDetail.user_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#5A7A9A]" />
                      <span className="text-[#00CFFF]">{selectedOportunidadDetail.user_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Urgente badge for necesito */}
              {oportunidadDetailType === 'necesito' && selectedOportunidadDetail.urgente && (
                <div className="bg-[#FF6B00]/10 rounded-lg p-4 border border-[#FF6B00]/30 flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-[#FF6B00]" />
                  <div>
                    <p className="font-bold text-[#FF6B00]">URGENTE</p>
                    <p className="text-sm text-[#5A7A9A]">El usuario necesita este material lo antes posible</p>
                  </div>
                </div>
              )}

              {/* Created date */}
              {selectedOportunidadDetail.created_at && (
                <p className="text-xs text-[#5A7A9A] text-center">
                  Creado: {new Date(selectedOportunidadDetail.created_at).toLocaleDateString('es-MX', { 
                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-[#1E3550] px-6 py-4 bg-[#0A1422] space-y-3">
              {/* WhatsApp Contact Buttons (Admin Only) */}
              {selectedOportunidadDetail.user_phone && (
                <Button
                  onClick={() => {
                    const phone = selectedOportunidadDetail.user_phone.replace(/\D/g, '');
                    const role = oportunidadDetailType === 'tengo' ? 'vendedor' : 'comprador';
                    const message = encodeURIComponent(
                      `Hola ${selectedOportunidadDetail.user_name}, soy de GenerIA Oportunidades. Te contacto por tu publicación de "${selectedOportunidadDetail.material}".`
                    );
                    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                  }}
                  className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contactar {oportunidadDetailType === 'tengo' ? 'Vendedor' : 'Comprador'} por WhatsApp
                </Button>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                {oportunidadDetailType === 'tengo' && selectedOportunidadDetail.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => {
                        handleApproveTengo(selectedOportunidadDetail.id);
                        setSelectedOportunidadDetail(null);
                      }}
                      className="flex-1 bg-[#22C55E] hover:bg-[#16A34A]"
                    >
                      <Check className="w-4 h-4 mr-2" /> Aprobar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleRejectTengo(selectedOportunidadDetail.id);
                        setSelectedOportunidadDetail(null);
                      }}
                      className="flex-1 border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                    >
                      <X className="w-4 h-4 mr-2" /> Rechazar
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    if (oportunidadDetailType === 'tengo') {
                      handleDeleteTengo(selectedOportunidadDetail.id);
                    } else {
                      handleDeleteNecesito(selectedOportunidadDetail.id);
                    }
                    setSelectedOportunidadDetail(null);
                  }}
                  className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Figure Prediction Detail Modal */}
      {showFigurePredictionDetail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1520] border border-[#1E3550] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-[#1E3550]">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-[#E2EAF4]">Comparación: Vision vs Residente</h2>
                  {selectedFigurePrediction?.photo_number && (
                    <span className="px-2 py-1 bg-[#06B6D4]/20 text-[#06B6D4] text-sm font-medium rounded">
                      Foto {String(selectedFigurePrediction.photo_number).padStart(3, '0')}
                    </span>
                  )}
                </div>
                {selectedFigurePrediction && (
                  <p className="text-sm text-[#5A7A9A] mt-1">
                    {selectedFigurePrediction.concept_clave} - {selectedFigurePrediction.concept_name}
                  </p>
                )}
                {selectedFigurePrediction?.id && (
                  <p className="text-xs text-[#5A7A9A]/60 mt-1 font-mono">
                    ID: {selectedFigurePrediction.id}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowFigurePredictionDetail(false);
                  setSelectedFigurePrediction(null);
                }}
                className="p-2 hover:bg-[#1E3550] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#5A7A9A]" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {/* Loading state */}
              {loadingFigureDetail ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <RefreshCw className="w-10 h-10 text-[#06B6D4] animate-spin mb-4" />
                  <span className="text-[#5A7A9A]">Cargando foto...</span>
                  <span className="text-xs text-[#5A7A9A]/60 mt-1">La imagen puede tardar unos segundos</span>
                </div>
              ) : selectedFigurePrediction ? (
              <>
              {/* Side by side comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* LEFT: Vision Proposal */}
                <div className="bg-[#060C18] rounded-xl border border-[#06B6D4]/30 overflow-hidden">
                  <div className="p-3 bg-[#06B6D4]/10 border-b border-[#06B6D4]/30">
                    <h3 className="font-semibold text-[#06B6D4] flex items-center gap-2">
                      <Brain className="w-4 h-4" />
                      GenerIA Vision
                    </h3>
                  </div>
                  <div className="p-4">
                    {/* Photo wrapper - EXACTLY like ConstructionCanvas */}
                    <div className="relative bg-[#0A1422] rounded-lg mb-3 overflow-hidden flex items-center justify-center" style={{ minHeight: '200px' }} data-testid="vision-figure-panel">
                      {selectedFigurePrediction.photo_base64 ? (
                        <>
                          <img 
                            ref={visionImgRef}
                            src={selectedFigurePrediction.photo_base64} 
                            alt="Foto de captura" 
                            onLoad={handleVisionImageLoad}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                          />
                          {/* SVG overlay - positioned exactly like ConstructionCanvas */}
                          {visionImageDims.width > 0 && selectedFigurePrediction.vision_figura?.puntos?.length > 0 && (
                            <svg
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: visionImageDims.width,
                                height: visionImageDims.height,
                                pointerEvents: 'none'
                              }}
                            >
                              <polygon
                                points={puntosToPixels(selectedFigurePrediction.vision_figura.puntos, visionImageDims.width, visionImageDims.height).map(p => `${p.x},${p.y}`).join(' ')}
                                fill="rgba(6, 182, 212, 0.2)"
                                stroke="#06B6D4"
                                strokeWidth="3"
                              />
                              {puntosToPixels(selectedFigurePrediction.vision_figura.puntos, visionImageDims.width, visionImageDims.height).map((p, i) => (
                                <circle key={i} cx={p.x} cy={p.y} r="5" fill="#06B6D4" />
                              ))}
                            </svg>
                          )}
                          {/* No puntos message */}
                          {!selectedFigurePrediction.vision_figura?.puntos?.length && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                              <p className="text-[#5A7A9A] text-sm bg-[#060C18]/80 px-3 py-1 rounded">Sin coordenadas de puntos</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-[#5A7A9A] text-sm">Sin foto disponible</p>
                      )}
                    </div>
                    <p className="text-xs text-[#5A7A9A] italic">
                      {selectedFigurePrediction.razonamiento || 'Sin razonamiento registrado'}
                    </p>
                  </div>
                </div>
                
                {/* RIGHT: Residente Capture */}
                <div className="bg-[#060C18] rounded-xl border border-[#FF6B00]/30 overflow-hidden">
                  <div className="p-3 bg-[#FF6B00]/10 border-b border-[#FF6B00]/30">
                    <h3 className="font-semibold text-[#FF6B00] flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Residente
                    </h3>
                  </div>
                  <div className="p-4">
                    {/* Photo wrapper - EXACTLY like ConstructionCanvas */}
                    <div className="relative bg-[#0A1422] rounded-lg mb-3 overflow-hidden flex items-center justify-center" style={{ minHeight: '200px' }} data-testid="residente-figure-panel">
                      {selectedFigurePrediction.photo_base64 ? (
                        <>
                          <img 
                            ref={residenteImgRef}
                            src={selectedFigurePrediction.photo_base64} 
                            alt="Foto de captura" 
                            onLoad={handleResidenteImageLoad}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                          />
                          {/* SVG overlay - positioned exactly like ConstructionCanvas */}
                          {residenteImageDims.width > 0 && selectedFigurePrediction.residente_figura?.puntos?.length > 0 && (
                            <svg
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: residenteImageDims.width,
                                height: residenteImageDims.height,
                                pointerEvents: 'none'
                              }}
                            >
                              <polygon
                                points={puntosToPixels(selectedFigurePrediction.residente_figura.puntos, residenteImageDims.width, residenteImageDims.height).map(p => `${p.x},${p.y}`).join(' ')}
                                fill={`${selectedFigurePrediction.residente_figura.color || '#FF6B00'}30`}
                                stroke={selectedFigurePrediction.residente_figura.color || '#FF6B00'}
                                strokeWidth="3"
                              />
                              {puntosToPixels(selectedFigurePrediction.residente_figura.puntos, residenteImageDims.width, residenteImageDims.height).map((p, i) => (
                                <circle key={i} cx={p.x} cy={p.y} r="5" fill={selectedFigurePrediction.residente_figura.color || '#FF6B00'} />
                              ))}
                            </svg>
                          )}
                          {/* No puntos message */}
                          {!selectedFigurePrediction.residente_figura?.puntos?.length && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                              <p className="text-[#5A7A9A] text-sm bg-[#060C18]/80 px-3 py-1 rounded">Sin coordenadas de puntos</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-[#5A7A9A] text-sm">Sin foto disponible</p>
                      )}
                    </div>
                    <p className="text-xs text-[#5A7A9A]">
                      Tipo: <span className="text-[#FF6B00] capitalize">{selectedFigurePrediction.residente_figura?.figura_type || '-'}</span>
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Summary */}
              <div className="bg-[#060C18] rounded-xl border border-[#1E3550] p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-[#5A7A9A] mb-1">Vision</p>
                    <p className="font-semibold text-[#06B6D4] capitalize">{selectedFigurePrediction.figura_type || '-'}</p>
                    <p className="text-xs text-[#5A7A9A]">Confianza: {selectedFigurePrediction.confidence || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#5A7A9A] mb-1">Residente</p>
                    <p className="font-semibold text-[#FF6B00] capitalize">
                      {selectedFigurePrediction.residente_figura?.figura_type || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#5A7A9A] mb-1">Match</p>
                    {selectedFigurePrediction.figura_match ? (
                      <div className="flex items-center justify-center gap-1 text-[#22C55E]">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-semibold">Mismo tipo</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1 text-[#EF4444]">
                        <XCircle className="w-5 h-5" />
                        <span className="font-semibold">Diferente tipo</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Additional Info */}
              <div className="mt-4 grid grid-cols-2 gap-4 text-xs text-[#5A7A9A]">
                <div>
                  <p>Calidad de foto: <span className="text-[#E2EAF4]">{selectedFigurePrediction.photo_quality || '-'}</span></p>
                  <p>Referencia: <span className="text-[#E2EAF4]">{selectedFigurePrediction.reference || '-'}</span></p>
                </div>
                <div>
                  <p>Unidad: <span className="text-[#E2EAF4]">{selectedFigurePrediction.unit}</span></p>
                  <p>Fecha: <span className="text-[#E2EAF4]">{selectedFigurePrediction.timestamp && new Date(selectedFigurePrediction.timestamp).toLocaleString('es-MX')}</span></p>
                </div>
              </div>
              </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Measurement Prediction Detail Modal */}
      {showMeasurementPredictionDetail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0D1520] border border-[#1E3550] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-[#1E3550]">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-[#E2EAF4]">Comparación: Vision vs Residente — Mediciones</h2>
                  {selectedMeasurementPrediction?.photo_number && (
                    <span className="px-2 py-1 bg-[#FF6B00]/20 text-[#FF6B00] text-sm font-medium rounded">
                      Foto {String(selectedMeasurementPrediction.photo_number).padStart(3, '0')}
                    </span>
                  )}
                </div>
                {selectedMeasurementPrediction && (
                  <p className="text-sm text-[#5A7A9A] mt-1">
                    {selectedMeasurementPrediction.concept_name}
                  </p>
                )}
                {selectedMeasurementPrediction?.id && (
                  <p className="text-xs text-[#5A7A9A]/60 mt-1 font-mono">
                    ID: {selectedMeasurementPrediction.id}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowMeasurementPredictionDetail(false);
                  setSelectedMeasurementPrediction(null);
                }}
                className="p-2 hover:bg-[#1E3550] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#5A7A9A]" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {/* Loading state */}
              {loadingMeasurementDetail ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <RefreshCw className="w-10 h-10 text-[#FF6B00] animate-spin mb-4" />
                  <span className="text-[#5A7A9A]">Cargando foto...</span>
                  <span className="text-xs text-[#5A7A9A]/60 mt-1">La imagen puede tardar unos segundos</span>
                </div>
              ) : selectedMeasurementPrediction ? (
              <>
              {/* Side by side comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* LEFT: Vision Measurement */}
                <div className="bg-[#060C18] rounded-xl border border-[#06B6D4]/30 overflow-hidden">
                  <div className="p-3 bg-[#06B6D4]/10 border-b border-[#06B6D4]/30">
                    <h3 className="font-semibold text-[#06B6D4] flex items-center gap-2">
                      <Brain className="w-4 h-4" />
                      GenerIA Vision
                    </h3>
                  </div>
                  <div className="p-4">
                    {/* Photo wrapper */}
                    <div className="relative bg-[#0A1422] rounded-lg mb-3 overflow-hidden flex items-center justify-center" style={{ minHeight: '200px' }} data-testid="measurement-vision-panel">
                      {selectedMeasurementPrediction.photo_base64 ? (
                        <>
                          <img 
                            ref={measurementVisionImgRef}
                            src={selectedMeasurementPrediction.photo_base64} 
                            alt="Foto de captura" 
                            onLoad={handleMeasurementVisionImageLoad}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                          />
                          {/* SVG overlay with figure outline and measurement lines */}
                          {measurementVisionImageDims.width > 0 && (
                            <svg
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: measurementVisionImageDims.width,
                                height: measurementVisionImageDims.height,
                                pointerEvents: 'none'
                              }}
                            >
                              {/* Figure outline */}
                              {selectedMeasurementPrediction.puntos?.length > 0 && (
                                <polygon
                                  points={puntosToPixels(selectedMeasurementPrediction.puntos, measurementVisionImageDims.width, measurementVisionImageDims.height).map(p => `${p.x},${p.y}`).join(' ')}
                                  fill="rgba(6, 182, 212, 0.15)"
                                  stroke="#06B6D4"
                                  strokeWidth="2"
                                />
                              )}
                              {/* Vision measurement lines */}
                              {selectedMeasurementPrediction.vision_medicion_coordinates?.map((coord, idx) => {
                                const x = coord.x * measurementVisionImageDims.width;
                                const y = coord.y * measurementVisionImageDims.height;
                                const isLargo = coord.punto?.includes('largo');
                                return (
                                  <g key={idx}>
                                    <circle cx={x} cy={y} r="6" fill={isLargo ? '#22C55E' : '#A855F7'} stroke="#fff" strokeWidth="1" />
                                    <text x={x + 10} y={y + 4} fill="#fff" fontSize="10" fontWeight="bold">
                                      {coord.punto?.replace('_', ' ')}
                                    </text>
                                  </g>
                                );
                              })}
                              {/* Draw lines between measurement points */}
                              {(() => {
                                const coords = selectedMeasurementPrediction.vision_medicion_coordinates || [];
                                const largoInicio = coords.find(c => c.punto === 'largo_inicio');
                                const largoFin = coords.find(c => c.punto === 'largo_fin');
                                const altoInicio = coords.find(c => c.punto === 'alto_inicio');
                                const altoFin = coords.find(c => c.punto === 'alto_fin');
                                return (
                                  <>
                                    {largoInicio && largoFin && (
                                      <line
                                        x1={largoInicio.x * measurementVisionImageDims.width}
                                        y1={largoInicio.y * measurementVisionImageDims.height}
                                        x2={largoFin.x * measurementVisionImageDims.width}
                                        y2={largoFin.y * measurementVisionImageDims.height}
                                        stroke="#22C55E"
                                        strokeWidth="3"
                                        strokeDasharray="8,4"
                                      />
                                    )}
                                    {altoInicio && altoFin && (
                                      <line
                                        x1={altoInicio.x * measurementVisionImageDims.width}
                                        y1={altoInicio.y * measurementVisionImageDims.height}
                                        x2={altoFin.x * measurementVisionImageDims.width}
                                        y2={altoFin.y * measurementVisionImageDims.height}
                                        stroke="#A855F7"
                                        strokeWidth="3"
                                        strokeDasharray="8,4"
                                      />
                                    )}
                                  </>
                                );
                              })()}
                            </svg>
                          )}
                        </>
                      ) : (
                        <p className="text-[#5A7A9A] text-sm">Sin foto disponible</p>
                      )}
                    </div>
                    {/* Vision measurements */}
                    <div className="bg-[#0A1422] rounded-lg p-3 mb-2">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-[#5A7A9A]">Largo:</span>
                        <span className="text-lg font-bold text-[#22C55E]">
                          {selectedMeasurementPrediction.vision_largo_m != null 
                            ? `${selectedMeasurementPrediction.vision_largo_m.toFixed(2)} m` 
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5A7A9A]">Alto:</span>
                        <span className="text-lg font-bold text-[#A855F7]">
                          {selectedMeasurementPrediction.vision_alto_m != null 
                            ? `${selectedMeasurementPrediction.vision_alto_m.toFixed(2)} m` 
                            : '-'}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-[#5A7A9A] italic">
                      {selectedMeasurementPrediction.vision_razonamiento || 'Sin razonamiento registrado'}
                    </p>
                  </div>
                </div>
                
                {/* RIGHT: Residente Measurement */}
                <div className="bg-[#060C18] rounded-xl border border-[#FF6B00]/30 overflow-hidden">
                  <div className="p-3 bg-[#FF6B00]/10 border-b border-[#FF6B00]/30">
                    <h3 className="font-semibold text-[#FF6B00] flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Residente
                    </h3>
                  </div>
                  <div className="p-4">
                    {/* Photo wrapper */}
                    <div className="relative bg-[#0A1422] rounded-lg mb-3 overflow-hidden flex items-center justify-center" style={{ minHeight: '200px' }} data-testid="measurement-residente-panel">
                      {selectedMeasurementPrediction.photo_base64 ? (
                        <>
                          <img 
                            ref={measurementResidenteImgRef}
                            src={selectedMeasurementPrediction.photo_base64} 
                            alt="Foto de captura" 
                            onLoad={handleMeasurementResidenteImageLoad}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                          />
                          {/* SVG overlay with residente's saved figure */}
                          {measurementResidenteImageDims.width > 0 && (
                            <svg
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: measurementResidenteImageDims.width,
                                height: measurementResidenteImageDims.height,
                                pointerEvents: 'none'
                              }}
                            >
                              {/* Use residente_figura if available, otherwise fall back to puntos */}
                              {(selectedMeasurementPrediction.residente_figura?.puntos || selectedMeasurementPrediction.puntos)?.length > 0 && (
                                <polygon
                                  points={puntosToPixels(
                                    selectedMeasurementPrediction.residente_figura?.puntos || selectedMeasurementPrediction.puntos, 
                                    measurementResidenteImageDims.width, 
                                    measurementResidenteImageDims.height
                                  ).map(p => `${p.x},${p.y}`).join(' ')}
                                  fill={`${selectedMeasurementPrediction.residente_figura?.color || '#FF6B00'}25`}
                                  stroke={selectedMeasurementPrediction.residente_figura?.color || '#FF6B00'}
                                  strokeWidth="2"
                                />
                              )}
                              {/* Draw vertices */}
                              {(selectedMeasurementPrediction.residente_figura?.puntos || selectedMeasurementPrediction.puntos)?.length > 0 && 
                                puntosToPixels(
                                  selectedMeasurementPrediction.residente_figura?.puntos || selectedMeasurementPrediction.puntos,
                                  measurementResidenteImageDims.width,
                                  measurementResidenteImageDims.height
                                ).map((p, i) => (
                                  <circle 
                                    key={i} 
                                    cx={p.x} 
                                    cy={p.y} 
                                    r="5" 
                                    fill={selectedMeasurementPrediction.residente_figura?.color || '#FF6B00'} 
                                  />
                                ))
                              }
                            </svg>
                          )}
                        </>
                      ) : (
                        <p className="text-[#5A7A9A] text-sm">Sin foto disponible</p>
                      )}
                    </div>
                    {/* Residente measurements */}
                    <div className="bg-[#0A1422] rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-[#5A7A9A]">Largo:</span>
                        <span className="text-lg font-bold text-[#FF6B00]">
                          {selectedMeasurementPrediction.residente_largo_m != null 
                            ? `${selectedMeasurementPrediction.residente_largo_m.toFixed(2)} m` 
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#5A7A9A]">Alto:</span>
                        <span className="text-lg font-bold text-[#FF6B00]">
                          {selectedMeasurementPrediction.residente_alto_m != null 
                            ? `${selectedMeasurementPrediction.residente_alto_m.toFixed(2)} m` 
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Precision Summary */}
              <div className="bg-[#060C18] rounded-xl border border-[#1E3550] p-4">
                <h4 className="text-sm font-semibold text-[#E2EAF4] mb-3">Análisis de Precisión</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className={`text-2xl font-bold ${
                      selectedMeasurementPrediction.precision_max_pct >= 90 ? 'text-[#22C55E]' :
                      selectedMeasurementPrediction.precision_max_pct >= 70 ? 'text-[#FFB800]' :
                      'text-[#EF4444]'
                    }`}>
                      {selectedMeasurementPrediction.precision_max_pct != null 
                        ? `${selectedMeasurementPrediction.precision_max_pct.toFixed(1)}%` 
                        : '-'}
                    </p>
                    <p className="text-xs text-[#5A7A9A]">Dimensión Mayor</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${
                      selectedMeasurementPrediction.precision_min_pct >= 90 ? 'text-[#22C55E]' :
                      selectedMeasurementPrediction.precision_min_pct >= 70 ? 'text-[#FFB800]' :
                      'text-[#EF4444]'
                    }`}>
                      {selectedMeasurementPrediction.precision_min_pct != null 
                        ? `${selectedMeasurementPrediction.precision_min_pct.toFixed(1)}%` 
                        : '-'}
                    </p>
                    <p className="text-xs text-[#5A7A9A]">Dimensión Menor</p>
                  </div>
                  <div>
                    <p className={`text-3xl font-bold ${
                      selectedMeasurementPrediction.precision_overall_pct >= 90 ? 'text-[#22C55E]' :
                      selectedMeasurementPrediction.precision_overall_pct >= 70 ? 'text-[#FFB800]' :
                      'text-[#EF4444]'
                    }`}>
                      {selectedMeasurementPrediction.precision_overall_pct != null 
                        ? `${selectedMeasurementPrediction.precision_overall_pct.toFixed(1)}%` 
                        : '-'}
                    </p>
                    <p className="text-xs text-[#5A7A9A]">Precisión General</p>
                  </div>
                </div>
              </div>
              
              {/* Additional Info */}
              <div className="mt-4 grid grid-cols-2 gap-4 text-xs text-[#5A7A9A]">
                <div>
                  <p>Calidad de foto: <span className="text-[#E2EAF4]">{selectedMeasurementPrediction.vision_photo_quality || '-'}</span></p>
                  <p>Referencia: <span className="text-[#E2EAF4]">{selectedMeasurementPrediction.vision_reference || '-'}</span></p>
                </div>
                <div>
                  <p>Confianza: <span className={`font-medium ${
                    selectedMeasurementPrediction.vision_confidence === 'high' ? 'text-[#22C55E]' :
                    selectedMeasurementPrediction.vision_confidence === 'medium' ? 'text-[#FFB800]' :
                    'text-[#EF4444]'
                  }`}>{selectedMeasurementPrediction.vision_confidence || '-'}</span></p>
                  <p>Fecha: <span className="text-[#E2EAF4]">{selectedMeasurementPrediction.timestamp && new Date(selectedMeasurementPrediction.timestamp).toLocaleString('es-MX')}</span></p>
                </div>
              </div>
              </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
